import { Trash2, ArrowLeft, CheckCircle, Plus, Minus, Lock, Info, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

// import { useToast } from "@/hooks/use-toast";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef, useCallback } from "react";
import { useCart } from "@/utils/cartContext";
import { addDays, differenceInCalendarDays, format, parse } from 'date-fns';
import { useQuery } from "@apollo/client";

import { CART_PAGE_ITEM, DELETE_CART_PAGE, REQUEST_FOR_APPROVAL, SUBMIT_ENQUIRY, COUNTRY_CODE_LIST } from "@/Graphql/Queries";
import { GET_MY_CART_ITEMS, ADD_TO_CART_SUBMIT } from "@/Graphql/cartQueries";
import { ADD_CAMPAIGN_CART } from "@/Graphql/packageQurie";
import { generateMockData } from "@/Graphql/MockData";
import client from "@/Graphql/apolloClient";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/utils/authContext";
import type { CartItem, AddedBillboardInput } from '../types/billboard';
import moment from "moment";
import { useMutation, gql } from "@apollo/client";
import React from "react";
import { getMediaOwner } from "@/data/media-owners";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface InventorySummary {
  classicInventories: number;
  digitalInventories: number;
  packages: number;
}

interface CampaignInventory {
  isSpotBased: boolean;
  billboardAddress: any;
  billboardVenueType: any;
  billboardPotentialViews: number;
  billboardId: string;
  billboardName: string;
  billboardType: string;
  billboardPeriodSubTotalPrice: number;
  billboardReferenceId: string;
  billboardLatitude: number;
  billboardLongitude: number;
  billboardResolutionWidth: string;
  billboardResolutionHeight: string;
  billboardThumbnailUrl: string;
  billboardUniqueReach: number;
  billboardFrequency: number;
  mediaOwnerId: string;
  mediaOwnerName: string;
  currencyCode?: string; // Add currency code field
  billboardCountry?: string; // Add billboard country field
}

interface CampaignData {
  id: string;
  userId: string;
  agencyId: string;
  agencyName: string;
  companyId: string;
  companyName: string;
  countryName: string;
  taxLabel?: string;
  taxPercent?: number;
  campaignName: string;
  startDate: string;
  endDate: string;
  inventoriesSummary: InventorySummary;
  overAlltotalNet: number;
  overAlltotalTax: number;
  overAlltotalPrice: number;
  overAlltotalUniqueReach: number;
  overAlltotalPotentialViews: number;
  overAlltotalAverageFrequency: number;
  totalDuration: number;
  campaignInventories: CampaignInventory[];
}

interface Agency {
  id: string;
  name: string;
}
interface InventorySummary {
  digitalInventories: number;
  classicInventories: number;
  packages: number;
}
interface Report {
  id: string;
  type: string;
  inventoryId: string;
  inventoryType: string;
  preferredPrice: string;
  availableBooking: string;
  billboardPreferredPrice: string;
  userId: string;
  cartItemId: string;
  campaignName: string;
  billboardPotentialViews: string;
  billboardType: string;
  agencyName: Agency;
  inventorySummary: InventorySummary;
  billboardName: string;
  billboardId: string;
  billboardThumbnailUrl: string;
  billboardPeriodSubTotalPrice: string;
  billboardLatitude: string;
  billboardVenueType: string;
  billboardLongitude: string;
  billboardResolutionWidth: string;
  billboardResolutionHeight: string;
  isDraft?: boolean; // Added isDraft as an optional property
}

interface Billboard {
  billboardId: any;
  priceSummary: any;
  id: number;

  inventoryThumbnailUrl: string | undefined;
  inventoryName: string;
  inventoryType: string;
  negotiationSummary: {
    status: string;
    summaryReport: {
      potentialViews: { toLocaleString: () => string };
      uniqueReach: { toLocaleString: () => string };
    };
    mediaOwnerPreferredPrice: {
      nowPayPrice: any;
      totalPrice: number;
    };
    buyerPreferredPrice: {
      nowPayPrice: any;
      totalPrice: number;
    };
  };
}
interface Billboard {
  billboardPotentialViews: number;
  billboardUniqueReach: number;
  billboardName: string;
  billboardType: string;
  billboardResolutionWidth: string;
  billboardResolutionHeight: string;
  billboardPeriodSubTotalPrice: number;
  billboardThumbnailUrl: string;
  availableBooking: boolean;
  billboardCurrencyCode?: string;
}


interface CampaignCartProps {
  currencyCode: any
  tax?: {
    label: string;
    percent: number;
  }
}


const formatDate = (dateInput: string | Date): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).replace(',', ''); // Remove comma between month and year
};

interface DeleteItem {
  id: string;
  itemType: string;
}

export default function Cart({ currencyCode: propCurrencyCode, tax: propTax }: CampaignCartProps) {
  const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
  const [submittingEnquiry, setSubmittingEnquiry] = useState(false);

  const { toast } = useToast();
  const { isLoggedIn } = useAuth();

  // Function to check if we're on the landing page
  const isLandingPage = () => {
    const currentPath = window.location.pathname;
    const basePath = import.meta.env.VITE_APP_BASE_PATH || '';
    // Check for common landing page patterns
    return currentPath === '/' ||
      currentPath === basePath ||
      currentPath === `${basePath}/` ||
      currentPath.endsWith('/landing') ||
      currentPath.endsWith('/home');
  };

  // Computed authentication state that considers mock data and landing page
  const shouldShowAsLoggedIn = () => {
    // If mock data is enabled and we're NOT on landing page, show as logged in
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true' && !isLandingPage()) {
      return true;
    }
    // Otherwise use the actual login state
    return isLoggedIn;
  };

  const effectiveIsLoggedIn = shouldShowAsLoggedIn();
  const {
    cartItems, removeFromCart, clearCart, totalCost, totalImpressions, cartCount, setCartItemCount,
    updateSpots, updateCampaignDuration, updateDaypartSchedules, totalUniqueReach, totalAverageFrequency,
    requiresCampaignDates, getAutomaticCampaignDates, hasInventoryTypeMix, getInventoryTypeBreakdown,
    getInventoryTypeProperties, taxLabel, taxPercent, priceFormat, cartCountryId, calculateTotalWithDuration,
    currentCampaignDuration, setCurrentCampaignDuration, setCartCountry, cartCountry,
    startDate, endDate, updateStartDate, updateEndDate
  } = useCart();

  // Add logging useEffect to track date changes
  useEffect(() => {
  }, [startDate, endDate]);

  // Use currency from cart items if available, otherwise fall back to prop
  const getCartCurrency = () => {
    if (cartItems.length > 0) {
      const firstItemCurrency = cartItems[0]?.currencyCode;
      if (firstItemCurrency && firstItemCurrency !== 'N/A') {
        return firstItemCurrency;
      }
    }

    const storedCurrencyCode = localStorage.getItem('currencyCode');
    if (storedCurrencyCode && storedCurrencyCode !== 'N/A') {
      return storedCurrencyCode;
    }
    const cartCountryId = localStorage.getItem('cartCountryId') || localStorage.getItem('sessionDefaultCountryId');
    return propCurrencyCode || 'N/A';
  };

  const currencyCode = getCartCurrency();

  // Helper function to get currency from campaign data
  const getCampaignCurrency = () => {
    if (campaignData?.campaignInventories && campaignData.campaignInventories.length > 0) {
      const firstItemCurrency = campaignData.campaignInventories[0]?.currencyCode;
      if (firstItemCurrency) {
        return firstItemCurrency;
      }
    }
    return billboardCurrencyCode || currencyCode;
  };

  // Helper function to get country name from billboard data
  const getCurrencyCountry = () => {

    // Priority 1: Campaign data billboard country
    if (campaignData?.campaignInventories && campaignData.campaignInventories.length > 0) {
      const firstItemCountry = campaignData.campaignInventories[0]?.billboardCountry;

      if (firstItemCountry) {
        return firstItemCountry;
      }
    }

    // Priority 2: Campaign data country name
    if (campaignData?.countryName) {
      return campaignData.countryName;
    }

    // Priority 3: Cart items country name
    if (cartItems && cartItems.length > 0 && cartItems[0]?.countryName) {
      return cartItems[0].countryName;
    }

    // Priority 4: sessionDefaultCountryName
    const storedCountryName = localStorage.getItem('sessionDefaultCountryName');
    if (storedCountryName) {
      return storedCountryName;
    }

    const fallbackCountry = 'Unknown';
    return fallbackCountry;
  };

  // Helper function to extract country from cart items
  const getCartCountry = (cartItems: any[]) => {
    console.log('[COUNTRY DEBUG] getCartCountry called with cartItems:', cartItems);
    if (!cartItems || cartItems.length === 0) {
      console.log('[COUNTRY DEBUG] getCartCountry: No cart items, returning null');
      return null;
    }
    const country = cartItems[0]?.billboardCountry ||
      cartItems[0]?.country ||
      cartItems[0]?.countryName ||
      cartItems[0]?.cartItemCountry ||  // ✅ ADD THIS: Reads the existing field from Explore
      null;
    console.log('[COUNTRY DEBUG] getCartCountry: Extracted country:', country, 'from first item:', cartItems[0]);
    return country;
  };

  // Campaign mode states (for authenticated users)
  const [campaignName, setCampaignName] = useState("");
  // Initialize dates
  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [draftCampaignId, setDraftCampaignId] = useState<string | null>(null);
  const [durationError, setDurationError] = useState("");

  // State for tracking API cart item spots
  const [apiCartSpots, setApiCartSpots] = useState<Record<string, number>>({});
  const [spotsUpdateLoading, setSpotsUpdateLoading] = useState<Record<string, boolean>>({});
  const [spotsUpdateError, setSpotsUpdateError] = useState<string>("");
  const debounceTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  const [deleteItems, setDeleteItems] = useState<DeleteItem[]>([]);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [clearedAllCart, setClearedAllCart] = useState(false);

  // Update clearedAllCart flag in localStorage when state changes
  useEffect(() => {
    if (clearedAllCart) {
      localStorage.setItem('clearedAllCart', 'true');
    } else {
      localStorage.removeItem('clearedAllCart');
    }
  }, [clearedAllCart]);

  // When the page loads, ensure clearedAllCart is false initially
  useEffect(() => {
    setClearedAllCart(false);
  }, []);

  // const [localCurrencyCode, setLocalCurrencyCode] = useState(currencyCode || "N/A");

  // useEffect(() => {
  //   if (!currencyCode) {
  //     const storedCurrencyCode = localStorage.getItem("currencyCode");
  //     if (storedCurrencyCode) {
  //       setLocalCurrencyCode(storedCurrencyCode);
  //     }
  //   }
  // }, [currencyCode]);
  // let storedCurrencyCodes = localStorage.getItem("currencyCode");
  const currentDate = new Date();
  // const resolvedCurrencyCode = storedCurrencyCodes || localCurrencyCode;

  // Enhanced number formatting function using toLocaleString
  const formatPrice = (amount: number, currencyCode?: string) => {
    if (currencyCode) {
      // Format with currency code and space, using comma separators
      const formattedAmount = amount?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return `${currencyCode?.toUpperCase()} ${formattedAmount}`;
    } else {
      // Use toLocaleString for number formatting only
      return amount?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  };


  const startDatePickerRef = useRef<HTMLDivElement>(null);
  const endDatePickerRef = useRef<HTMLDivElement>(null);
  // Read from localStorage for the initial render, fallback to new Date() if not present
  const defaultExploreStartDate = format(
    localStorage.getItem('startDate')
      ? new Date(localStorage.getItem('startDate')!)
      : new Date(),
    'yyyy-MM-dd'
  );

  const defaultExploreEndDate = format(
    localStorage.getItem('endDate')
      ? new Date(localStorage.getItem('endDate')!)
      : new Date(),
    'yyyy-MM-dd'
  );
  const [redirect, setRedirect] = useState<string | null>(null);
  const [showStartDatePicker, setShowStartDatePicker] = useState<boolean>(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState<boolean>(false);
  const [tempStartDate, setTempStartDate] = useState<Date>(startDate || currentDate || "");
  const [tempEndDate, setTempEndDate] = useState<Date>(endDate || currentDate || "");
  const [potentialViews, setPotentialViews] = useState<string>("");
  const [uniqueReach, setUniqueReach] = useState<string>("");
  const [totalNet, setTotalNet] = useState<string>("");
  const [overAlltotalTax, setOverAlltotalTax] = useState<number>(0);
  const [alltotalPrice, setAlltotalPrice] = useState<string>("");
  const [averageFrequency, setAverageFrequency] = useState<string>("");
  const [inventorySummary, setInventorySummary] = useState<InventorySummary | null>(null); // Initialize as null
  const [dateChangeCounter, setDateChangeCounter] = useState(0); // Force API calls when dates change
  const [cartList, setCartList] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNegotiationCheckedProof, setIsNegotiationCheckedProof] = useState(true);
  const [billboardCurrencyCode, setBillboardCurrencyCode] = useState<string>("");
  const [selectedBillboard, setSelectedBillboard] = useState<string[]>([]);
  const [digitalBillboardLength, setDigitalBillboardLength] = useState(0);
  const [classicBillboardLength, setClassicBillboardLength] = useState(0);

  const [campaignId, setCampaignId] = useState<string | null>(localStorage.getItem('selectedCampaignId'));
  const [edit, setEdit] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('edit') === 'true';
  });
  const [source, setSource] = useState('');
  const [itemType, setItemType] = useState<boolean>(false);
  const [campaignType, setcampaignType] = useState<boolean>(false);
  const [available, setAvailable] = useState('')

  const [isNegotiationCheckedDelivery, setIsNegotiationCheckedDelivery] = useState(true);
  const [isNegotiationCheckedPostCampaign, setIsNegotiationCheckedPostCampaign] = useState(true);

  // submit enquiry flow:

  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Track if localStorage cart has been synced to prevent duplicate syncing
  const [hasLocalStorageSynced, setHasLocalStorageSynced] = useState(() => {
    // Only check if sync has been done if user is currently logged in
    const isCurrentlyLoggedIn = !!localStorage.getItem('authToken');
    if (!isCurrentlyLoggedIn) {
      return false; // Don't mark as synced for non-logged in users
    }
    // Check if sync has already been done for this session
    return sessionStorage.getItem('cartSyncCompleted') === 'true';
  });

  // Add force sync state for explore-login-flow
  const [forceSync, setForceSync] = useState(false);

  // Track if explore deduplication has been processed to prevent multiple executions
  const [hasProcessedExploreData, setHasProcessedExploreData] = useState(false);

  // Track if sync is currently in progress to prevent concurrent calls
  const [isSyncing, setIsSyncing] = useState(false);

  const [isSubmittingForApproval, setIsSubmittingForApproval] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Country conflict detection states
  const [showCountryConflictModal, setShowCountryConflictModal] = useState(false);
  const [serverCartCountry, setServerCartCountry] = useState<string | null>(null);
  const [localCartCountry, setLocalCartCountry] = useState<string | null>(null);
  const [hasCountryConflict, setHasCountryConflict] = useState(false);

  // New state to capture the local cart country at login time (before server override)
  const [initialLocalCartCountry, setInitialLocalCartCountry] = useState<string | null>(null);

  // Track if explore deduplication has been processed to prevent multiple executions
  // This is now handled within syncLocalStorageCartToAPI

  // Set currency code from first cart item for local cart (before login)
  useEffect(() => {
    if (!effectiveIsLoggedIn && cartItems.length > 0) {
      const firstItemCurrency = cartItems[0]?.currencyCode;
      if (firstItemCurrency && firstItemCurrency !== 'N/A') {
        setBillboardCurrencyCode(firstItemCurrency);
        // Store currency code in localStorage for persistence across page refreshes
        localStorage.setItem('currencyCode', firstItemCurrency);
      }
    } else if (!effectiveIsLoggedIn && cartItems.length === 0) {
      // Clear stored currency when cart is empty and user is not logged in
      const storedCurrency = localStorage.getItem('currencyCode');
      if (storedCurrency) {
        // Don't clear immediately - keep it for potential future cart additions
      }
    }
  }, [cartItems, effectiveIsLoggedIn]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startDatePickerRef.current && !startDatePickerRef.current.contains(event.target as Node)) {
        setShowStartDatePicker(false);
      }
      if (endDatePickerRef.current && !endDatePickerRef.current.contains(event.target as Node)) {
        setShowEndDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      updateStartDate(date); // Context handles validation and localStorage
      setTempStartDate(date);

      // Force API call by incrementing counter
      setDateChangeCounter(prev => prev + 1);

      if (campaignId) {
        localStorage.setItem(`campaign_${campaignId}_startDate`, format(date, 'yyyy-MM-dd'));
      }

      // Update campaign data with the new dates
      setCampaignData((prev) => {
        if (!prev) return prev;

        // If end date was adjusted by context, update both
        const newEndDate = endDate;
        return {
          ...prev,
          startDate: format(date as Date, 'yyyy-MM-dd'),
          endDate: format(newEndDate as Date, 'yyyy-MM-dd')
        };
      });

      // Force update the display
      const formattedStartDate = format(date, 'dd MMM yyyy');
      const formattedEndDate = format(endDate as Date, 'dd MMM yyyy');
      const startDateElements = document.querySelectorAll('.date-display span');
      if (startDateElements && startDateElements.length > 0) {
        startDateElements[0].textContent = formattedStartDate;
        if (startDateElements.length > 1) {
          startDateElements[1].textContent = formattedEndDate;
        }
      }

      setShowStartDatePicker(false); // Close calendar automatically
      // Remove direct fetchCartList call - let useEffect handle it with debouncing
    }
  };

  // Sync temp dates with context dates
  useEffect(() => {
    if (startDate) {
      setTempStartDate(startDate);
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setTempEndDate(endDate);
    }
  }, [endDate]);

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      updateEndDate(date); // Context handles validation and localStorage
      setTempEndDate(date);

      // Force API call by incrementing counter
      setDateChangeCounter(prev => prev + 1);

      if (campaignId) {
        localStorage.setItem(`campaign_${campaignId}_endDate`, format(date, 'yyyy-MM-dd'));
      }

      // Update campaign data with the new date
      setCampaignData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          endDate: format(date, 'yyyy-MM-dd')
        };
      });

      // Force update the display
      const formattedEndDate = format(date, 'dd MMM yyyy');
      const dateElements = document.querySelectorAll('.date-display span');
      if (dateElements && dateElements.length > 1) {
        dateElements[1].textContent = formattedEndDate;
      }

      setShowEndDatePicker(false); // Close calendar automatically
      // Remove direct fetchCartList call - let useEffect handle it with debouncing
    }
  };


  //Cart Config
  const [config, setConfig] = useState<any[]>([]);
  const [configQuick, setConfigQuick] = useState<any[]>([]);
  const [configShowReport, setConfigShowReport] = useState<any[]>([]);
  const [isLoginEnabled, setIsLoginEnabled] = useState(true);
  const [isExploreEnabled, setIsExploreEnabled] = useState(true);

  const [terms, setConfigTerms] = useState<any[]>([]);
  const [reportconfig, setReportConfig] = useState<any[]>([]);
  const [isCartPageEnabled, setIsCartPageEnabled] = useState(true);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()};`);
      const data = await response.json();
      const value = data.find((page: any) => page.name === "Cart");
      // Reduced logging - only log significant events
      if (!value) {
        console.warn("Cart configuration not found");
      }
      const quickConfig = value?.configurations?.find((page: any) => page.key === "Quick Summary");
      setConfigQuick(quickConfig?.fields || []);
      const showReportConfig = value?.configurations?.find((page: any) => page.key === "show report in cart page?");
      setConfigShowReport(showReportConfig?.fields || []);

      // Get login configuration from Landing Page
      const landingPageConfig = data.find((page: any) => page.name === "Landing Page");
      const loginConfig = landingPageConfig?.configurations?.find((cfg: any) => cfg.key === "login");
      setIsLoginEnabled(loginConfig ? loginConfig.default : true);

      // Get explore configuration from Landing Page
      const exploreConfig = landingPageConfig?.configurations?.find((cfg: any) => cfg.key === "Explore");
      const isExploreEnabledLocal = exploreConfig ? exploreConfig.default : true;
      setIsExploreEnabled(isExploreEnabledLocal);

      const Termsvalue = data.find((page: any) => page.name === "Terms & conditions");
      const reportconfig = data.find((page: any) => page.name === "Reports");

      // If the configuration is found, set it in state
      if (Termsvalue && Termsvalue.configurations) {
        setConfigTerms(Termsvalue.configurations);
        const cartConfig = Termsvalue.configurations.find((cfg: any) => cfg.key === "cartpage");
        setIsCartPageEnabled(cartConfig ? cartConfig.default : true);
      } else {
        console.error("Terms & conditions configuration not found.");
      }
      setConfig(value?.configurations || []);
      setReportConfig(reportconfig.configurations || []);

    } catch (error) {
      console.error("Error fetching configuration:", error);
    }
  };

  // Check if the field is enabled based on the configuration
  // Replace the existing isFieldEnabled function with this improved version
  const isFieldEnabled = (key: string, config: any[]) => {
    if (!config || !Array.isArray(config)) {
      return true; // Default to true if config is invalid
    }

    const fieldConfig = config.find(field => field?.key === key);
    if (!fieldConfig) {
      return true; // Default to true if field not found
    }

    // Special handling for radio type fields
    if (fieldConfig.type === 'radio') {
      return fieldConfig.default === 'yes';
    }

    // Handle boolean default values for switch type fields
    if (fieldConfig.type === 'switch') {
      return !!fieldConfig.default;
    }

    // For other field types or nested structures
    if (config.some(cfg => cfg.fields)) {
      const nestedConfig = config
        .filter(cfg => cfg.fields)
        .flatMap(cfg => cfg.fields)
        .find(field => field?.key === key);
      return nestedConfig?.default ?? true;
    }

    // Default case
    return fieldConfig?.default ?? true;
  };

  const isFieldEnabledTerms = (key: string, terms: any[]) => {
    const fieldConfig = terms.find((field) => field.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };


  useEffect(() => {
    fetchConfig();
    // Reduced logging - only log on errors or important events
  }, []);

  const [content, setContent] = useState<any[]>([]);
  const [termss, setTerms] = useState<string>("");
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()};`);
        const data = await response.json();
        const value = data.find((page: any) => page.name === "Landing Page");
        setContent(value.fields);
        const values = data.find((page: any) => page.name === "Terms & conditions");
        const termLen = (values.fields[0].components[0].components).length;
        for (let i = 0; i < termLen; i++) {
          if ('key' in values.fields[0].components[0].components[i]) {
            if (values.fields[0].components[0].components[i].key === "terms_conditionsDocument") {
              if ((values.fields[0].components[0].components[i].defaultValue).length > 0) {
                setTerms(values.fields[0].components[0].components[i].defaultValue[0].data.text)
                break;
              }
            }
            else {
              if (values.fields[0].components[0].components[i].key === "TermsText") {
                setTerms(values.fields[0].components[0].components[i].defaultValue)
              }
            }
          }
        }

      } catch (error) {
      }
    };

    fetchContent();
  }, []);

  // const { data: myCart, error, refetch } = useQuery(GET_MY_CART_ITEMS, {
  //     variables: {
  //         accessToken: token,
  //         userId: userId,
  //     },

  // });




  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Save campaign data to localStorage
  useEffect(() => {
    if (campaignData) {
      localStorage.setItem('campaignDataBackup', JSON.stringify({
        campaignName: campaignData.campaignName,
        startDate: campaignData.startDate,
        endDate: campaignData.endDate,
      }));
    }
  }, [campaignData?.campaignName, campaignData?.startDate, campaignData?.endDate]);

  const fetchCartList = async () => {

    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }

    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(CART_PAGE_ITEM)
        .then((mockResponse: any) => {
          const data = mockResponse.data;
          const campaignDatas = data.cartPageItem[0];

          if (!campaignDatas) {
            // Preserve existing campaign data if response is empty
            setCartList([]); // Clear cart list but keep campaign data
            setInventorySummary(null);
            setCartItemCount(0);
            setLoading(false);
            return;
          }

          // Update only specific fields, preserve others
          setCampaignData((prev) => ({
            ...prev, // Retain existing campaign data
            ...campaignDatas, // Update with new data
            campaignName: prev?.campaignName || campaignDatas.campaignName || campaignName, // Preserve campaign name
            startDate: prev?.startDate || campaignDatas.startDate, // Preserve start date
            endDate: prev?.endDate || campaignDatas.endDate, // Preserve end date
          }));
          setApiTaxLabel(campaignDatas.taxLabel || '');
          setApiTaxPercent(campaignDatas.taxPercent || 0);
          setInventorySummary(campaignDatas.inventoriesSummary || null);
          setCartList(campaignDatas.campaignInventories || []);
          // Deduplicate campaignInventories by billboardId
          const uniqueInventories = [];
          const seenIds = new Set();
          for (const item of (campaignDatas.campaignInventories || [])) {
            const id = item.billboardId || item.id || item.cartItemId;
            if (!seenIds.has(id)) {
              uniqueInventories.push(item);
              seenIds.add(id);
            }
          }
          setCartList(uniqueInventories);
          setCartItemCount(uniqueInventories.length);
          if (uniqueInventories.length > 0) {
            const firstBillboard = uniqueInventories.map((item: any) => item.billboardId);
            setSelectedBillboard(firstBillboard);
          } else {
            setSelectedBillboard([]);
          }

          // Initialize API cart spots from loaded data
          const initialSpots: Record<string, number> = {};
          uniqueInventories.forEach((item: any) => {
            if (item.cartItemId && item.userSelectedSpots) {
              initialSpots[item.cartItemId] = item.userSelectedSpots;
            } else if (item.cartItemId && item.minimumSpotsPerDay) {
              initialSpots[item.cartItemId] = item.minimumSpotsPerDay;
            }
          });
          setApiCartSpots(initialSpots);

          setLoading(false);

          // Extract currency from first campaign inventory item instead of using prop
          const campaignCurrency = uniqueInventories.length > 0 && uniqueInventories[0]?.currencyCode
            ? uniqueInventories[0].currencyCode
            : currencyCode;
          setBillboardCurrencyCode(campaignCurrency);
          setDigitalBillboardLength(campaignDatas?.inventoriesSummary?.digitalInventories || 0);
          setClassicBillboardLength(campaignDatas?.inventoriesSummary?.classicInventories || 0);

          setPotentialViews(campaignDatas?.overAlltotalPotentialViews || 0);
          setUniqueReach(campaignDatas?.overAlltotalUniqueReach || 0);
          setTotalNet(campaignDatas?.overAlltotalPrice || 0);
          setOverAlltotalTax(campaignDatas?.overAlltotalTax || 0);
          setAlltotalPrice(campaignDatas?.overAlltotalNet || 0);
          setAverageFrequency(campaignDatas?.overAlltotalAverageFrequency?.toFixed(2) || "0");
          setAvailable(campaignDatas?.availableBooking ? 'true' : 'false');
        })
        .catch((err: any) => {
          console.error("Error generating mock data:", err);
          toast({
            title: "Error",
            description: "Failed to fetch cart data.",
            variant: "destructive"
          });
          setLoading(false);
        });
    } else {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");

      if (!token) {
        toast({
          title: "Error",
          description: "No authentication token found.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        if (!startDate || !endDate) {
          // toast({
          //   title: "Error",
          //   description: "Invalid date range.",
          //   variant: "destructive"
          // });
          setLoading(false);
          return;
        }
        const formattedStartDate = format(startDate, 'dd MMM yyyy');
        const formattedEndDate = format(endDate, 'dd MMM yyyy');

        // Build billboard spots array for API call
        const billboardSpots = buildBillboardSpotsArray();

        const variables = {
          accessToken: token,
          campaignId: campaignId || "",
          userId: userId,
          edit: edit,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          exposure: import.meta.env.VITE_APP_EXPOSURE,
          // Dynamically set billboardSpots if there are any
          billboardSpots: billboardSpots.length > 0 ? billboardSpots.map(spot => ({
            billboardId: spot.billboardId,
            billboardType: spot.billboardType,
            spotDuration: spot.spotDuration
          })) : []
        };

        // Always use CART_PAGE_ITEM for GraphQL queries
        const response = await client.query({
          query: CART_PAGE_ITEM,
          variables: variables,
          fetchPolicy: "network-only"
        });

        if (response.errors) {
          console.error("GraphQL Errors:", response.errors);
          toast({
            title: "Error",
            description: "Failed to fetch reports: " + response.errors.map(err => err.message).join(", "),
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        const campaignDatas = response.data.cartPageItem[0];

        if (!campaignDatas) {
          const backupData = localStorage.getItem('campaignDataBackup');
          if (backupData) {
            const parsedBackup = JSON.parse(backupData);
            setCampaignData((prev: any) => ({
              ...prev,
              campaignName: parsedBackup.campaignName || campaignName,
              startDate: parsedBackup.startDate || startDate,
              endDate: parsedBackup.endDate || endDate,
            }));
          }
          setCartList([]);
          setInventorySummary(null);
          setCartItemCount(0);
          setLoading(false);
          return;
        }

        // Update only specific fields, preserve others
        setCampaignData((prev) => ({
          ...prev,
          ...campaignDatas,
          campaignName: campaignName || prev?.campaignName || campaignDatas.campaignName,
          startDate: prev?.startDate || campaignDatas.startDate,
          endDate: prev?.endDate || campaignDatas.endDate,
        }));

        // Check if this is a draft flow - skip setting cartCountry for drafts
        const urlParams = new URLSearchParams(window.location.search);
        const isDraft = urlParams.get('edit') === 'true';

        // Extract and set country from cart data to prevent reset (only for non-drafts)
        if (campaignDatas?.campaignInventories?.[0]?.billboardCountry && !isDraft) {
          const cartCountryCode = campaignDatas.campaignInventories[0].billboardCountry;
          localStorage.setItem('cartCountry', cartCountryCode);
          setCartCountry(cartCountryCode);
        } else if (isDraft) {
          console.warn('Skipping cart country set for draft - will be handled by start-campaign');
        }

        setApiTaxLabel(campaignDatas.taxLabel || '');
        setApiTaxPercent(campaignDatas.taxPercent || 0);
        setInventorySummary(campaignDatas.inventoriesSummary || null);
        setCartList(campaignDatas.campaignInventories || []);
        setCartItemCount(campaignDatas.campaignInventories?.length || 0);

        // Initialize API cart spots from loaded data
        const initialSpots: Record<string, number> = {};
        (campaignDatas.campaignInventories || []).forEach((item: any) => {
          if (item.cartItemId && item.userSelectedSpots) {
            initialSpots[item.cartItemId] = item.userSelectedSpots;
          } else if (item.cartItemId && item.minimumSpotsPerDay) {
            initialSpots[item.cartItemId] = item.minimumSpotsPerDay;
          }
        });
        setApiCartSpots(initialSpots);

        if (campaignDatas.campaignInventories && campaignDatas.campaignInventories.length > 0) {
          const firstBillboard = campaignDatas.campaignInventories.map((item: any) => item.billboardId);
          setSelectedBillboard(firstBillboard);
        } else {
          setSelectedBillboard([]);
        }

        setLoading(false);
        setBillboardCurrencyCode(currencyCode);

        setPotentialViews(campaignDatas?.overAlltotalPotentialViews || 0);
        setUniqueReach(campaignDatas?.overAlltotalUniqueReach || 0);
        setTotalNet(campaignDatas?.overAlltotalPrice || 0);
        setOverAlltotalTax(campaignDatas?.overAlltotalTax || 0);
        setAlltotalPrice(campaignDatas?.overAlltotalNet || 0);
        setAverageFrequency(campaignDatas?.overAlltotalAverageFrequency?.toFixed(2) || "0");
        setAvailable(campaignDatas?.availableBooking ? 'true' : 'false');
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    }
  };

  // Initial fetch function to get campaign data including dates from API
  const fetchInitialCartData = async () => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(CART_PAGE_ITEM)
        .then((mockResponse: any) => {
          const data = mockResponse.data;
          const campaignDatas = data.cartPageItem[0];

          if (!campaignDatas) {
            // Set default dates if no campaign data
            updateStartDate(today);
            updateEndDate(today);
            setCartList([]);
            setInventorySummary(null);
            setCartItemCount(0);
            setLoading(false);
            return;
          }

          // Set dates from API response
          if (campaignDatas.startDate) {
            const apiStartDate = new Date(campaignDatas.startDate);
            updateStartDate(apiStartDate);
          } else {
            updateStartDate(today);
          }

          if (campaignDatas.endDate) {
            const apiEndDate = new Date(campaignDatas.endDate);
            updateEndDate(apiEndDate);
          } else {
            updateEndDate(today);
          }

          // Update campaign data and other states
          setCampaignData(campaignDatas);

          // Check if this is a draft flow - skip setting cartCountry for drafts
          const urlParams = new URLSearchParams(window.location.search);
          const isDraft = urlParams.get('edit') === 'true';

          // Extract and set country from cart data to prevent reset (only for non-drafts)
          if (campaignDatas?.campaignInventories?.[0]?.billboardCountry && !isDraft) {
            const cartCountryCode = campaignDatas.campaignInventories[0].billboardCountry;
            localStorage.setItem('cartCountry', cartCountryCode);
            setCartCountry(cartCountryCode);
          } else if (isDraft) {
            console.warn('Skipping cart country set for draft - will be handled by start-campaign');
          }

          setApiTaxLabel(campaignDatas.taxLabel || '');
          setApiTaxPercent(campaignDatas.taxPercent || 0);
          setInventorySummary(campaignDatas.inventoriesSummary || null);
          setCartList(campaignDatas.campaignInventories || []);
          setCartItemCount(campaignDatas.campaignInventories?.length || 0);

          if (campaignDatas.campaignInventories && campaignDatas.campaignInventories.length > 0) {
            const firstBillboard = campaignDatas.campaignInventories.map((item: any) => item.billboardId);
            setSelectedBillboard(firstBillboard);
          } else {
            setSelectedBillboard([]);
          }

          setLoading(false);
          // Extract currency from campaign data (mock)
          const campaignCurrency = campaignDatas?.campaignInventories?.length > 0 && campaignDatas.campaignInventories[0]?.currencyCode
            ? campaignDatas.campaignInventories[0].currencyCode
            : currencyCode;
          setBillboardCurrencyCode(campaignCurrency);
          setDigitalBillboardLength(campaignDatas?.inventoriesSummary?.digitalInventories || 0);
          setClassicBillboardLength(campaignDatas?.inventoriesSummary?.classicInventories || 0);

          setPotentialViews(campaignDatas?.overAlltotalPotentialViews || 0);
          setUniqueReach(campaignDatas?.overAlltotalUniqueReach || 0);
          setTotalNet(campaignDatas?.overAlltotalPrice || 0);
          setOverAlltotalTax(campaignDatas?.overAlltotalTax || 0);
          setAlltotalPrice(campaignDatas?.overAlltotalNet || 0);
          setAverageFrequency(campaignDatas?.overAlltotalAverageFrequency?.toFixed(2) || "0");
          setAvailable(campaignDatas?.availableBooking ? 'true' : 'false');
        })
        .catch((err: any) => {
          console.error("Error generating mock data:", err);
          toast({
            title: "Error",
            description: "Failed to fetch cart data.",
            variant: "destructive"
          });
          // Set default dates on error
          updateStartDate(today);
          updateEndDate(today);
          setLoading(false);
        });
    } else {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");

      if (!token) {
        toast({
          title: "Error",
          description: "No authentication token found.",
          variant: "destructive"
        });
        // Set default dates when no token
        updateStartDate(today);
        updateEndDate(today);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // --- [DEBUG] LOGS START ---
        // --- START OF FIX ---
        // Get dates from CONTEXT first. The context already loaded them from localStorage.
        // `startDate` and `endDate` are from the `useCart()` hook.
        console.log(`[DEBUG] fetchInitialCartData: Reading from context: startDate='${startDate}', endDate='${endDate}'`);

        let apiStartDate, apiEndDate;
        let datesWereSetFromLocalStorage = false; // We re-use this flag to mean "dates came from context"

        if (startDate && endDate) {
          console.log("[DEBUG] fetchInitialCartData: DATES FOUND in context. Using them.");
          // 1. DATES FOUND IN CONTEXT (Explore Flow)
          // Use context dates for API call
          const savedStartDate = startDate;
          const savedEndDate = endDate;

          // DO NOT call updateStartDate/updateEndDate, they are already correct.
          console.log("[DEBUG] fetchInitialCartData: Context already has dates. Skipping update.");

          apiStartDate = format(savedStartDate, 'dd MMM yyyy');
          apiEndDate = format(savedEndDate, 'dd MMM yyyy');
          datesWereSetFromLocalStorage = true;
        } else {
          // Use default date range for initial fetch
          apiStartDate = format(today, 'dd MMM yyyy');
          apiEndDate = format(today, 'dd MMM yyyy');
          datesWereSetFromLocalStorage = false;
        }

        // Build billboard spots array for initial load (if any exist in state)
        const billboardSpots = buildBillboardSpotsArray();

        const variables = {
          accessToken: token,
          campaignId: campaignId || "",
          userId: userId,
          edit: edit,
          startDate: apiStartDate, // Uses dates from localStorage OR today
          endDate: apiEndDate,     // Uses dates from localStorage OR today
          exposure: import.meta.env.VITE_APP_EXPOSURE,
          // Dynamically set billboardSpots if there are any
          billboardSpots: billboardSpots.length > 0 ? billboardSpots.map(spot => ({
            billboardId: spot.billboardId,
            billboardType: spot.billboardType,
            spotDuration: spot.spotDuration
          })) : []
        };

        // Always use CART_PAGE_ITEM for GraphQL queries
        const response = await client.query({
          query: CART_PAGE_ITEM,
          variables: variables,
          fetchPolicy: "network-only"
        });

        if (response.errors) {
          console.error("GraphQL Errors:", response.errors);
          toast({
            title: "Error",
            description: "Failed to fetch reports: " + response.errors.map(err => err.message).join(", "),
            variant: "destructive"
          });
          // Set default dates on error only if they weren't from localStorage
          if (!datesWereSetFromLocalStorage) {
            updateStartDate(today);
            updateEndDate(today);
          }
          setLoading(false);
          return;
        }

        const campaignDatas = response.data.cartPageItem[0];

        if (!campaignDatas) {
          console.log("[DEBUG] fetchInitialCartData: No campaign data in API response.");
          // Set default dates if no campaign data, only if they weren't from localStorage
          if (!datesWereSetFromLocalStorage) {
            updateStartDate(today);
            updateEndDate(today);
          }
          setCartList([]);
          setInventorySummary(null);
          setCartItemCount(0);
          // ✅ FIX: Reset ALL country conflict states when cart is empty
          console.log('------Test-----> BEFORE clearing country states (no campaign data):', {
            serverCartCountry,
            localCartCountry,
            hasCountryConflict,
            initialLocalCartCountry
          });
          setServerCartCountry('empty');
          setLocalCartCountry(null);
          setHasCountryConflict(false);
          setInitialLocalCartCountry(null);
          console.log('------Test-----> AFTER clearing country states (no campaign data):', {
            serverCartCountry: 'empty',
            localCartCountry: null,
            hasCountryConflict: false,
            initialLocalCartCountry: null
          });
          console.log('[COUNTRY CONFLICT] Reset all country states - no campaign data');
          setLoading(false);
          return;
        }

        // 3. SET DATES FROM API (ONLY IF NOT ALREADY SET FROM LOCALSTORAGE)
        if (!datesWereSetFromLocalStorage) {
          console.log("[DEBUG] fetchInitialCartData: datesWereSetFromLocalStorage is FALSE. Setting dates from API response.");
          // If we didn't have localStorage dates, use the API response dates
          if (campaignDatas.startDate) {
            const apiStartDateFromResponse = new Date(campaignDatas.startDate);
            console.log(`[DEBUG] fetchInitialCartData: Setting startDate from API: ${apiStartDateFromResponse.toISOString()}`);
            updateStartDate(apiStartDateFromResponse);
          } else {
            console.log(`[DEBUG] fetchInitialCartData: No API startDate. Setting startDate to 'today'.`);
            updateStartDate(today);
          }

          if (campaignDatas.endDate) {
            const apiEndDateFromResponse = new Date(campaignDatas.endDate);
            console.log(`[DEBUG] fetchInitialCartData: Setting endDate from API: ${apiEndDateFromResponse.toISOString()}`);
            updateEndDate(apiEndDateFromResponse);
          } else {
            console.log(`[DEBUG] fetchInitialCartData: No API endDate. Setting endDate to 'today'.`);
            updateEndDate(today);
          }
        } else {
          console.log("[DEBUG] fetchInitialCartData: datesWereSetFromLocalStorage is TRUE. SKIPPING date set from API response.");
        }
        // --- END OF FIX ---

        // Update campaign data and other states (Rest of the function)
        setCampaignData(campaignDatas);

        // Check if this is a draft flow - skip setting cartCountry for drafts
        const urlParams = new URLSearchParams(window.location.search);
        const isDraft = urlParams.get('edit') === 'true';

        // Extract and set country from cart data to prevent reset (only for non-drafts)
        if (campaignDatas?.campaignInventories?.[0]?.billboardCountry && !isDraft) {
          const cartCountryCode = campaignDatas.campaignInventories[0].billboardCountry;
          localStorage.setItem('cartCountry', cartCountryCode);
          setCartCountry(cartCountryCode);
        } else if (isDraft) {
          //   const cartCountryId = localStorage.getItem('sessionDefaultCountryId');

          //   if(campaignDatas?.campaignInventories?.length === 0)
          //   localStorage.setItem('sessionDefaultCountryId', cartCountryId ?? '');
          // console.warn(cartCountryId, 'draft cart country id');

          console.warn('Skipping cart country set for draft - will be handled by start-campaignmfmf');
        }

        setApiTaxLabel(campaignDatas.taxLabel || '');
        setApiTaxPercent(campaignDatas.taxPercent || 0);
        setInventorySummary(campaignDatas.inventoriesSummary || null);
        setCartList(campaignDatas.campaignInventories || []);
        setCartItemCount(campaignDatas.campaignInventories?.length || 0);

        // Set server cart country for conflict detection
        if (campaignDatas?.campaignInventories?.length > 0) {
          const serverCountry = getCartCountry(campaignDatas.campaignInventories);
          setServerCartCountry(serverCountry);
        } else {
          // ✅ FIX: Reset ALL country conflict states when cart is empty
          console.log('------Test-----> BEFORE clearing country states (empty inventories):', {
            serverCartCountry,
            localCartCountry,
            hasCountryConflict,
            initialLocalCartCountry
          });
          setServerCartCountry('empty');
          setLocalCartCountry(null);
          setHasCountryConflict(false);
          setInitialLocalCartCountry(null);
          console.log('------Test-----> AFTER clearing country states (empty inventories):', {
            serverCartCountry: 'empty',
            localCartCountry: null,
            hasCountryConflict: false,
            initialLocalCartCountry: null
          });
          console.log('[COUNTRY CONFLICT] Reset all country states - empty cart inventories');
        }

        // Initialize API cart spots from loaded data
        const initialSpots: Record<string, number> = {};
        (campaignDatas.campaignInventories || []).forEach((item: any) => {
          if (item.cartItemId && item.userSelectedSpots) {
            initialSpots[item.cartItemId] = item.userSelectedSpots;
          } else if (item.cartItemId && item.minimumSpotsPerDay) {
            initialSpots[item.cartItemId] = item.minimumSpotsPerDay;
          }
        });
        setApiCartSpots(initialSpots);

        if (campaignDatas.campaignInventories && campaignDatas.campaignInventories.length > 0) {
          const firstBillboard = campaignDatas.campaignInventories.map((item: any) => item.billboardId);
          setSelectedBillboard(firstBillboard);
        } else {
          setSelectedBillboard([]);
        }

        setLoading(false);
        setBillboardCurrencyCode(currencyCode);

        setPotentialViews(campaignDatas?.overAlltotalPotentialViews || 0);
        setUniqueReach(campaignDatas?.overAlltotalUniqueReach || 0);
        setTotalNet(campaignDatas?.overAlltotalPrice || 0);
        setOverAlltotalTax(campaignDatas?.overAlltotalTax || 0);
        setAlltotalPrice(campaignDatas?.overAlltotalNet || 0);
        setAverageFrequency(campaignDatas?.overAlltotalAverageFrequency?.toFixed(2) || "0");
        setAvailable(campaignDatas?.availableBooking ? 'true' : 'false');
      } catch (err) {
        console.error("Error fetching initial cart data:", err);
        // Set default dates on error
        updateStartDate(today);
        updateEndDate(today);
        setLoading(false);
      }
    }
  };

  // Function to fetch cart count using GET_MY_CART_ITEMS API
  const fetchCartCountDetails = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        return;
      }

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        try {
          const mockData = await generateMockData(GET_MY_CART_ITEMS);
          const count = mockData.data?.cartpageItem?.length || 0;
          setCartItemCount(count);
          window.dispatchEvent(new Event('cartUpdated'));
        } catch (error) {
          console.error("🛒 Error generating mock cart data:", error);
          setCartItemCount(0);
        }
      } else {
        try {
          const { data } = await client.query({
            query: GET_MY_CART_ITEMS,
            fetchPolicy: "network-only",
            variables: {
              accessToken: token,
              userId: userId,
            }
          });

          const count = data?.cartpageItem?.length || 0;
          setCartItemCount(count);
          window.dispatchEvent(new Event('cartUpdated'));
        } catch (error) {
          console.error("🛒 Error fetching cart count:", error);
          setCartItemCount(0);
        }
      }
    } catch (error) {
      console.error("🛒 Error in fetchCartCountDetails:", error);
      setCartItemCount(0);
    }
  };

  // useEffect(() => {

  //     const digitaluniqueBillboards = cartList.filter(
  //         (inventory, index, self) =>
  //             index === self.findIndex((item) => item.billboardId === inventory.billboardId) &&
  //             (inventory.billboardType === "digital")
  //     );
  //     const classicuniqueBillboards = cartList.filter(
  //         (inventory, index, self) =>
  //             index === self.findIndex((item) => item.billboardId === inventory.billboardId) &&
  //             (inventory.billboardType === "classic")
  //     );
  //     setDigitalBillboardLength(digitaluniqueBillboards.length);
  //     setClassicBillboardLength(classicuniqueBillboards.length);
  // }, [cartList]);



  // const [inputValue, setInputValue] = useState('');
  // const [negotiate, { loading: sendRequest, error: sendError }] = useMutation(NEGOTIATE_CAMPAIGN_SEND_REQUEST);

  const [gstValue, setGstValue] = useState('')
  const [percentage, setPercentage] = useState('')
  const [apiTaxLabel, setApiTaxLabel] = useState<string>('');
  const [apiTaxPercent, setApiTaxPercent] = useState<number>(0);

  useEffect(() => {
    // Only use API tax data if it has meaningful values
    if (apiTaxLabel && apiTaxLabel !== '' && apiTaxPercent !== undefined && apiTaxPercent > 0) {
      setGstValue(apiTaxLabel);
      setPercentage(apiTaxPercent.toString());
      return;
    }

    if (!isLoggedIn) {
      setGstValue(taxLabel || '');
      setPercentage(taxPercent ? taxPercent.toString() : '');
      return; // Exit if user is not logged in
    }

    // For logged-in users, use userAccountData as fallback
    const userAccountData = localStorage.getItem('userAccountData');

    const parsedData = JSON.parse(userAccountData ?? '[]');
    if (parsedData && parsedData[0]?.country?.tax) {
      try {
        setGstValue(parsedData[0]?.country?.tax?.label || '');
        setPercentage(parsedData[0]?.country?.tax?.percent?.toString() || '');
      } catch (error) {
        console.error("Error parsing JSON:", error);
      }
    } else {
      console.error("No user account data found in local storage.");
    }
  }, [isLoggedIn, taxLabel, taxPercent, apiTaxLabel, apiTaxPercent]);


  const handleAcknowledgeClose = () => {
    setShowAcknowledgeModal(false);
    // Simply close the modal without redirecting
  };


  // CONSOLIDATED: Fetch cart data when dates change (with debounce)
  // Only trigger when dates are loaded AND user is logged in
  useEffect(() => {
    if (!isLoggedIn || loading || !startDate || !endDate) return;

    const timeoutId = setTimeout(() => {
      if (startDate && endDate && !loading) {
        fetchCartList();
      }
    }, 1000); // Debounce to prevent rapid repeated calls

    return () => clearTimeout(timeoutId);
  }, [startDate, endDate, isLoggedIn]); // Keep dependencies minimal

  // CONSOLIDATED: Fetch initial cart data when user logs in (one-time only)
  useEffect(() => {
    if (isLoggedIn && !loading && !campaignData) {
      const timeoutId = setTimeout(() => {
        fetchInitialCartData();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoggedIn]); // Only trigger on login state change

  // Sync localStorage cart to API when user logs in (only once per session)
  useEffect(() => {
    // Only sync if user is logged in, hasn't synced yet, is not currently syncing, and has auth data
    // Remove the startDate and endDate dependency to prevent repeated calls
    if (isLoggedIn && (!hasLocalStorageSynced || forceSync) && !isSyncing && localStorage.getItem('authToken') && serverCartCountry !== null) {
      const timeoutId = setTimeout(() => {
        syncLocalStorageCartToAPI();
        setForceSync(false); // Reset force sync flag
      }, 300); // Reduce timeout for faster sync

      return () => clearTimeout(timeoutId);
    } else {
      console.log('[SYNC useEffect] Skipping sync call:', {
        isLoggedIn,
        hasLocalStorageSynced,
        forceSync,
        isSyncing,
        hasAuthToken: !!localStorage.getItem('authToken'),
        serverCartCountry,
        reason: !isLoggedIn ? 'not logged in' :
          (hasLocalStorageSynced && !forceSync) ? 'already synced' :
            isSyncing ? 'already syncing' :
              !localStorage.getItem('authToken') ? 'no auth token' :
                serverCartCountry === null ? 'server cart country not loaded yet' : 'unknown'
      });
    }
  }, [isLoggedIn, hasLocalStorageSynced, isSyncing, forceSync, hasCountryConflict, serverCartCountry]); // Add forceSync to dependencies

  // Add useEffect to detect explore-login-flow
  useEffect(() => {
    // Check if coming from explore-login-flow (including package-specific keys)
    const urlParams = new URLSearchParams(window.location.search);
    const fromExplore = urlParams.get('from') === 'explore' ||
      localStorage.getItem('preLoginCartData') ||
      localStorage.getItem('preLoginExploreFlowData') ||
      localStorage.getItem('billboardCart') ||
      localStorage.getItem('exploreBillboardArray') ||
      localStorage.getItem('packageCart') ||
      localStorage.getItem('preLoginPackageData') ||
      localStorage.getItem('explorePackageArray') ||
      localStorage.getItem('packageBillboardCart') ||
      localStorage.getItem('dealCart');

    if (isLoggedIn && fromExplore && !hasLocalStorageSynced) {
      setForceSync(true);
    }
  }, [isLoggedIn, hasLocalStorageSynced]);

  // Capture initial local cart country when user logs in (before server data overwrites cartCountry)
  useEffect(() => {
    if (isLoggedIn && !initialLocalCartCountry && cartItems.length > 0) {
      // Extract country from cartItems which contains detail page data
      const localCountry = getCartCountry(cartItems);
      const countryId = cartItems[0]?.countryId || localStorage.getItem('selectedCountryId') || 'N/A';
      const countryName = localCountry || cartItems[0]?.countryName || 'N/A';
      
      if (localCountry) {
        console.log('[COUNTRY CONFLICT] Capturing initial local cart country at login:', {
          countryName: countryName,
          countryId: countryId,
          country: localCountry
        });
        setInitialLocalCartCountry(localCountry);
      }
    }
    // Reset if cartCountry is cleared (after submission or cart clear)
    if (isLoggedIn && initialLocalCartCountry && !cartCountry) {
      console.log('[COUNTRY CONFLICT] Resetting initialLocalCartCountry - cartCountry is now empty');
      setInitialLocalCartCountry(null);
    }
  }, [isLoggedIn, cartCountry, initialLocalCartCountry]);

  // Country conflict detection useEffect
  useEffect(() => {
    console.log('[COUNTRY CONFLICT] Checking for conflicts...');

    // Only check for conflicts when user is logged in
    if (!isLoggedIn) {
      setHasCountryConflict(false);
      setShowCountryConflictModal(false);
      return;
    }

    // Early exit if server cart is empty - no conflict possible
    if (serverCartCountry === 'empty' || serverCartCountry === null) {
      console.log('[COUNTRY CONFLICT] Skipping conflict check - server cart is empty');
      setHasCountryConflict(false);
      setShowCountryConflictModal(false);
      return;
    }

    // Get local cart country: Prefer initialLocalCartCountry (captured at login), fallback to extracting from items
    const localCountry = cartItems.length > 0 ? (initialLocalCartCountry || getCartCountry(cartItems)) : null;

    // Check if we have both server and local cart countries
    if (serverCartCountry === null || localCountry === null) {
      setHasCountryConflict(false);
      setLocalCartCountry(null);
      return;
    }

    if (serverCartCountry === 'empty') {
      setHasCountryConflict(false);
      setShowCountryConflictModal(false);
      setLocalCartCountry(null);
      return;
    }

    // Check if countries are different
    const hasConflict = serverCartCountry.toLowerCase() !== localCountry.toLowerCase();
    setHasCountryConflict(hasConflict);
    setShowCountryConflictModal(hasConflict);
    
    // Set localCartCountry state for modal display
    if (hasConflict) {
      setLocalCartCountry(localCountry);
      console.log('[COUNTRY CONFLICT] Detected conflict:', {
        serverCountry: serverCartCountry,
        localCountry: localCountry
      });
    }
  }, [isLoggedIn, serverCartCountry, initialLocalCartCountry]);

  // Helper function to build billboard spots array
  const buildBillboardSpotsArray = useCallback((currentCartList?: any[], overrideSpots?: Record<string, number>) => {
    const listToUse = currentCartList || cartList;
    return listToUse
      .filter(item => item.billboardType?.toLowerCase() === 'digital')
      .map(item => ({
        billboardId: item.billboardId,
        billboardType: item.billboardType?.toLowerCase() || 'digital',
        spotDuration: overrideSpots?.[item.cartItemId] ||
          apiCartSpots[item.cartItemId] ||
          item.userSelectedSpots ||
          item.minimumSpotsPerDay || 1
      }));
  }, [cartList, apiCartSpots]);

  // Helper function to build GraphQL query with billboard spots
  // const buildCartPageItemQuery = useCallback((variables: any, billboardSpots: any[]) => {
  //   return `
  //     query {
  //       cartPageItem(
  //         accessToken: "${variables.accessToken}"
  //         userId: "${variables.userId}"
  //         campaignId: "${variables.campaignId || ""}"
  //         startDate: "${variables.startDate}"
  //         endDate: "${variables.endDate}"
  //         edit: ${variables.edit || false}
  //         ${billboardSpots.length > 0 ? `billboardSpots: [
  //           ${billboardSpots.map(spot => `{
  //             billboardId: "${spot.billboardId}"
  //             billboardType: "${spot.billboardType}"
  //             spotDuration: ${spot.spotDuration}
  //           }`).join(',\n            ')}
  //         ]` : ''}
  //         exposure: "${import.meta.env.VITE_APP_EXPOSURE}"
  //       ) {
  //         id
  //         userId
  //         agencyId
  //         agencyName
  //         companyId
  //         companyName
  //         countryId
  //         countryName
  //         campaignName
  //         startDate
  //         endDate
  //         inventoriesSummary {
  //           classicInventories
  //           digitalInventories
  //           packages
  //         }
  //         overAlltotalNet
  //         overAlltotalTax
  //         overAlltotalPrice
  //         overAlltotalUniqueReach
  //         overAlltotalPotentialViews
  //         overAlltotalAverageFrequency
  //         totalDuration
  //         campaignInventories {
  //           billboardPotentialViews
  //           cartItemId
  //           billboardId
  //           billboardName
  //           billboardType
  //           billboardAddress
  //           billboardCountry
  //           billboardDeviceId
  //           billboardReferenceId
  //           billboardLatitude
  //           billboardVenueType
  //           billboardFrequency
  //           billboardLongitude
  //           billboardUniqueReach
  //           billboardResolutionWidth
  //           billboardResolutionHeight
  //           billboardPeriodSubTotalPrice
  //           billboardThumbnailUrl
  //           billboardCurrencyCode
  //           isDraft
  //           availableBooking
  //           mediaOwnerId
  //           mediaOwnerName
  //           minimumSellingDays
  //           minimumSellingHour
  //           minimumSpotsPerDay
  //           validityStartDate
  //           validityEndDate
  //           campaignDuration
  //           maximumSpotDurationPerDay
  //           customizeSellingTerms
  //           userSelectedSpots
  //           isSpotBased
  //           spotPrice
  //           singleSpotCost
  //           spotDuration
  //         }
  //       }
  //     }
  //   `;
  // }, []);

  // Debounced API update function for spots
  const updateSpotsViaAPI = useCallback(async (billboardId: string, spotDuration: number, billboardType: string) => {
    try {
      setSpotsUpdateLoading(prev => ({ ...prev, [billboardId]: true }));
      setSpotsUpdateError("");

      // Build billboardSpots array with current spots and override for the specific billboard
      const targetItem = cartList.find(item => item.billboardId === billboardId);
      const overrideSpots: Record<string, number> = {};
      if (targetItem?.cartItemId) {
        overrideSpots[targetItem.cartItemId] = spotDuration;
      }
      const billboardSpots = buildBillboardSpotsArray(cartList, overrideSpots);

      const variables = {
        accessToken: localStorage.getItem('authToken'),
        userId: localStorage.getItem('userId'),
        campaignId: "", // Set the campaignId if needed
        startDate: campaignData?.startDate || format(startDate || new Date(), 'yyyy-MM-dd'),
        endDate: campaignData?.endDate || format(endDate || new Date(), 'yyyy-MM-dd'),
        edit: false,
        exposure: "MARKETPLACE", // If needed, you can add this as well
        // Dynamically set billboardSpots if there are any
        billboardSpots: billboardSpots.length > 0 ? billboardSpots.map(spot => ({
          billboardId: spot.billboardId,
          billboardType: spot.billboardType,
          spotDuration: spot.spotDuration
        })) : []
      };

      // Always use CART_PAGE_ITEM for GraphQL queries
      const response = await client.query({
        query: CART_PAGE_ITEM,
        variables: variables,
        fetchPolicy: 'network-only'
      });

      if (response.data?.cartPageItem?.[0]) {
        const updatedData = response.data.cartPageItem[0];

        // Update cart list with new prices and spot data
        const updatedInventories = updatedData.campaignInventories || [];
        setCartList(updatedInventories);
        setCampaignData(updatedData);

        // Update the spots state with confirmed values
        const updatedSpots: Record<string, number> = {};
        updatedInventories.forEach((item: any) => {
          if (item.userSelectedSpots && item.cartItemId) {
            updatedSpots[item.cartItemId] = item.userSelectedSpots;
          }
        });
        setApiCartSpots(prev => ({ ...prev, ...updatedSpots }));
      }
    } catch (error) {
      console.error('Error updating spots:', error);
      setSpotsUpdateError('Failed to update spots. Please try again.');
    } finally {
      setSpotsUpdateLoading(prev => ({ ...prev, [billboardId]: false }));
    }
  }, [cartList, campaignData, startDate, endDate, apiCartSpots]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editFlag = urlParams.get('edit') === 'true';
    if (editFlag !== edit) {
      setEdit(editFlag);
    }
  }, []); // Remove dependencies to prevent loops, run only on mount

  // Debounced wrapper function
  const debouncedUpdateSpots = useCallback((billboardId: string, spotDuration: number, billboardType: string) => {
    // Clear existing timeout for this billboard
    if (debounceTimeouts.current[billboardId]) {
      clearTimeout(debounceTimeouts.current[billboardId]);
    }

    // Set new timeout
    debounceTimeouts.current[billboardId] = setTimeout(() => {
      updateSpotsViaAPI(billboardId, spotDuration, billboardType);
    }, 1000); // 1 second debounce
  }, [updateSpotsViaAPI]);

  const handleDeleteItem = (id: string, isDraft: boolean) => {
    setDeleteItemId(id);
    setShowDeleteModal(true);
    // Optionally update itemType state if needed elsewhere
    setItemType(isDraft); // Set itemType to true for draft items, false for cart items
  };

  const resetCampaignDatesToToday = () => {
    // Only reset dates if this is NOT a draft campaign
    const urlParams = new URLSearchParams(window.location.search);
    const isDraftMode = urlParams.get('edit') === 'true' || urlParams.get('draftId');

    if (isDraftMode) {
      console.log("🔄 Draft campaign detected - skipping date reset");
      return;
    } else {
      const today = new Date();
      updateStartDate(today);
      updateEndDate(today);
      console.log("🔄 Campaign dates reset to today");
    }
  };

  const handleClearAllClick = () => {
    // Set clearedAllCart to true when user clicks "Clear All"
    setClearedAllCart(true);
    localStorage.setItem('clearedAllCart', 'true');
    console.log("🗑️ User initiated 'Clear All' - clearedAllCart set to true");

    // ✅ FIX: Sync country filter before clearing
    // Get the current cart country and update the filter
    if (cartCountryId) {
      localStorage.setItem('selectedCountryId', cartCountryId);
      localStorage.setItem('sessionDefaultCountryId', cartCountryId);
    }

    // Reset dates when clearing cart
    resetCampaignDatesToToday();

    // Prepare all items for deletion
    const itemsToDelete = cartList.map((item: any) => ({
      id: item.cartItemId,
      itemType: item.isDraft ? "draft-data" : "cart-data"
    }));
    setDeleteItems(itemsToDelete);
    setShowDeleteAllModal(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem("authToken");
    if (!token || !deleteItemId) return;

    setIsSubmitting(true);

    const billboardToDelete = cartList.find((item: any) => item.cartItemId === deleteItemId);
    const isDraft = billboardToDelete?.isDraft || false;
    const itemTypeValue = isDraft ? "draft-data" : "cart-data";

    try {
      // Perform deletion using GraphQL mutation
      const response = await client.mutate({
        mutation: DELETE_CART_PAGE,
        variables: {
          accessToken: token,
          deleteItems: [{
            id: deleteItemId,
            itemType: itemTypeValue
          }]
        },
        fetchPolicy: "no-cache",
      });

      const success = response?.data?.deleteCartItem?.success;
      const message = response?.data?.deleteCartItem?.message || (success ? "Cart item deleted successfully" : "Failed to delete cart item");

      if (success) {
        toast({
          title: "Success",
          description: message,
          variant: "default"
        });

        // ✅ FIX: Ensure proper UI refresh
        try {
          // First update local state immediately for UI responsiveness
          setCartList((prev) => prev.filter((item: any) => item.cartItemId !== deleteItemId));
          setCartItemCount(Math.max(0, cartCount - 1));

          // Then fetch fresh data from API with proper error handling
          setLoading(false); // Ensure loading state is clear before fetch
          // Only call fetchCartList - it will update cart count internally
          await fetchCartList();
        } catch (fetchError) {
          console.error("❌ Error fetching updated cart data:", fetchError);
          // Show user-friendly message but don't fail the delete operation
          toast({
            title: "Warning",
            description: "Item deleted successfully, but cart data may need a page refresh to update fully.",
            variant: "default"
          });
        }

        // Update other local states
        const updatedDigitalCount = cartList.filter(
          (item: any) => item.billboardType === "digital" && item.cartItemId !== deleteItemId
        ).length;
        const updatedClassicCount = cartList.filter(
          (item: any) => item.billboardType === "classic" && item.cartItemId !== deleteItemId
        ).length;

        setDigitalBillboardLength(updatedDigitalCount);
        setClassicBillboardLength(updatedClassicCount);
        setInventorySummary({
          digitalInventories: updatedDigitalCount,
          classicInventories: updatedClassicCount,
          packages: cartItems.filter(item => item.type?.toLowerCase() === 'package').length,
        });

        setSelectedBillboard((prev) => prev.filter((id) => id !== billboardToDelete?.billboardId));

        // Ensure edit state is correct for draft items before fetching updated data
        if (isDraft && !edit) {
          setEdit(true);
        }

        // Fetch updated cart data from server
        try {
          await fetchCartList();

        } catch (fetchError) {
          console.error("Error fetching updated cart data:", fetchError);
          toast({
            title: "Error",
            description: "Cart item deleted, but failed to refresh cart data. Please refresh the page.",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Error",
          description: message,
          variant: "destructive"
        });
      }

      setShowDeleteModal(false);
    } catch (error) {
      console.error("❌ Deletion error:", error);
      toast({
        title: "Error",
        description: "Failed to delete content",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setDeleteItemId(null);
    }
  };

  const confirmDeleteAll = async () => {
    if (!isLoggedIn) {
      // ✅ FIX: Sync country filter for non-logged-in users
      if (cartCountryId) {
        localStorage.setItem('selectedCountryId', cartCountryId);
        localStorage.setItem('sessionDefaultCountryId', cartCountryId);
      }

      // For non-logged-in users, just use the context clearCart
      clearCart();
      resetCampaignDatesToToday();
      setShowDeleteAllModal(false);
      setClearedAllCart(true); // Set to true after clearing
      return;
    }

    if (cartList.length === 0) {
      setShowDeleteAllModal(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      if (!token || deleteItems.length === 0) return;

      setIsSubmitting(true);

      const response = await client.mutate({
        mutation: DELETE_CART_PAGE,
        variables: {
          accessToken: token,
          deleteItems: deleteItems
        },
        fetchPolicy: "no-cache",
      });

      const success = response?.data?.deleteCartItem?.success;
      const message = response?.data?.deleteCartItem?.message || (success ? "All cart items deleted successfully" : "Failed to delete cart items");

      if (success) {
        toast({
          title: "Success",
          description: message,
          variant: "default"
        });

        // ✅ FIX: Sync country filter after clearing
        if (cartCountryId) {
          localStorage.setItem('selectedCountryId', cartCountryId);
          localStorage.setItem('sessionDefaultCountryId', cartCountryId);
        }

        // ✅ FIX: Ensure proper UI refresh
        try {
          // First clear local state immediately
          setCartList([]);
          setCartItemCount(0);
          setDigitalBillboardLength(0);
          setClassicBillboardLength(0);
          setInventorySummary({ digitalInventories: 0, classicInventories: 0, packages: 0 });
          setSelectedBillboard([]);
          setClearedAllCart(true); // Set to true after clearing

          // Reset campaign dates to today after clearing cart
          resetCampaignDatesToToday();

          // Then fetch fresh data from API with proper error handling  
          setLoading(false); // Ensure loading state is clear before fetch
          // Only call fetchCartList - it will update cart count internally
          await fetchCartList();
        } catch (fetchError) {
          console.error("❌ Error fetching updated cart data after delete all:", fetchError);
          toast({
            title: "Warning",
            description: "Items deleted successfully, but cart data may need a page refresh to update fully.",
            variant: "default"
          });
        }

      } else {
        toast({
          title: "Error",
          description: message,
          variant: "destructive"
        });
      }

      setShowDeleteAllModal(false);
      setDeleteItems([]);
    } catch (error) {
      console.error("❌ Bulk deletion error:", error);
      toast({
        title: "Error",
        description: "Failed to delete all items",
        variant: "destructive"
      });
      setShowDeleteAllModal(false);
      setDeleteItems([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteItemId(null);
  };


  //   handle proof 

  const handleProof = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    const newValue = event.target.checked;
    setIsNegotiationCheckedProof(newValue);
  };

  const handleDelivery = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    const newValue = event.target.checked;
    setIsNegotiationCheckedDelivery(newValue);
  };

  // Commented out for now as requested
  // const handlePostCampaign = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
  //   const newValue = event.target.checked;
  //   console.log('Post Campaign Report checkbox toggled:', newValue ? 'checked' : 'unchecked');
  //   setIsNegotiationCheckedPostCampaign(newValue);
  // };

  // Add this state for checkbox error styling
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto hide success modal after 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showSuccessModal) {
      timer = setTimeout(() => {
        handleSuccessClose();
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessModal]);

  const navigate = useNavigate();

  // Only use redirect functionality when explicitly navigating away from the page
  useEffect(() => {
    if (redirect) {
      window.location.href = redirect; // Forces a full page reload
    }
  }, [redirect]);

  // Auto-hide success modal after 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showSuccessModal) {
      timer = setTimeout(() => {
        handleSuccessClose();
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessModal]);


  const [highlightCheckbox, setHighlightCheckbox] = useState(false);

  // Function to sync localStorage cart data to API when user logs in
  const syncLocalStorageCartToAPI = async (ignoreConflictState: boolean = false, targetCountry: string | null = null) => {
    console.log("[COUNTRY CONFLICT] cartlist length in sync--->", cartList, cartItems);

    console.log("[COUNTRY CONFLICT] targetCountry---------->", targetCountry, "ignoreConflictState--------->", ignoreConflictState);
    try {
      // Add force sync logic for explore-login-flow
      if (forceSync) {
        setHasLocalStorageSynced(false); // Reset sync flag
        sessionStorage.removeItem('cartSyncCompleted'); // Reset session storage
      }

      // Check if this is a draft flow - skip sync for drafts
      const urlParams = new URLSearchParams(window.location.search);
      const isDraft = urlParams.get('edit') === 'true' || urlParams.get('draftId');

      if (isDraft) {
        console.log('[COUNTRY CONFLICT] [SYNC] Skipping sync - draft campaign detected');
        return; // Skip sync for drafts
      }

      if (!isLoggedIn) {
        console.log('[COUNTRY CONFLICT] [SYNC] Skipping sync - user not logged in');
        return;
      }

      // Check for country conflict - prevent sync if there's a conflict
      console.log('[COUNTRY CONFLICT] [SYNC] Checking for country conflict:', {
        hasCountryConflict,
        serverCartCountry,
        localCartCountry,
        cartCountry,
        isLoggedIn,
        ignoreConflictState
      });
      if (hasCountryConflict && !ignoreConflictState) {
        console.log('[COUNTRY CONFLICT] [SYNC] Skipping sync - country conflict detected - preventing merge of mixed-country carts');
        return;
      }

      if (hasLocalStorageSynced && !forceSync && !ignoreConflictState) {
        console.log('[COUNTRY CONFLICT] [SYNC] Skipping sync - cart sync already completed for this session');
        return;
      }
      if (isSyncing && !ignoreConflictState) {
        console.log('[COUNTRY CONFLICT] [SYNC] Skipping sync - cart sync already in progress');
        return;
      }

      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) {
        console.error('[SYNC] Authentication data not found - skipping sync');
        return;
      }

      console.log('[COUNTRY CONFLICT] [SYNC] Starting cart sync process...', { targetCountry });
      setIsSyncing(true);

      // Helper to get normalized ID
      const getNormalizedId = (item: any) => item.billboardId || item.id || item.cartItemId;

      // Fetch existing cart items from API for deduplication
      let existingCartItems = [];
      try {
        const { data } = await client.query({
          query: GET_MY_CART_ITEMS,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            userId: userId,
          }
        });

        existingCartItems = data?.cartpageItem || [];
      } catch (error) {
        console.error('[COUNTRY CONFLICT] [SYNC] Error fetching existing cart items:', error);
        setIsSyncing(false); // Ensure we stop syncing on error
        return;
      }

      // Create set of existing cart item IDs for deduplication
      const existingCartIds = new Set(existingCartItems.map((item: any) => item.cartItemId));

      // Collect items from multiple sources
      let itemsToSync: any[] = [];

      // 1. Get preserved pre-login cart items (from login flow)
      const preLoginCartData = localStorage.getItem('preLoginCartData');
      if (preLoginCartData) {
        try {
          const cartItems = JSON.parse(preLoginCartData);

          if (Array.isArray(cartItems) && cartItems.length > 0) {
            itemsToSync = [...itemsToSync, ...cartItems];
          }
        } catch (error) {
          console.error('[SYNC] Error parsing preLoginCartData:', error);
        }
      }

      // 2. Fallback: Get localStorage cart items (billboardCart) - for other scenarios
      if (itemsToSync.length === 0) {
        const localStorageCart = localStorage.getItem('billboardCart');
        if (localStorageCart) {
          try {
            const cartItems = JSON.parse(localStorageCart);
            if (Array.isArray(cartItems) && cartItems.length > 0) {
              itemsToSync = [...itemsToSync, ...cartItems];
            }
          } catch (error) {
            console.error('[SYNC] Error parsing billboardCart:', error);
          }
        }
      }

      // 2. Get explore billboard items (from explore flow)
      const exploreBillboardRaw = localStorage.getItem('preLoginExploreFlowData') || localStorage.getItem('exploreBillboardArray');
      console.log("localstorage - preLoginExploreFlowData", JSON.parse(localStorage.getItem('preLoginExploreFlowData') || '[]'));
      console.log("localstorage - exploreBillboardArray", JSON.parse(localStorage.getItem('exploreBillboardArray') || '[]'));

      if (exploreBillboardRaw) {
        try {
          const exploreBillboardArray = JSON.parse(exploreBillboardRaw);
          if (Array.isArray(exploreBillboardArray) && exploreBillboardArray.length > 0) {
            itemsToSync = [...itemsToSync, ...exploreBillboardArray];
          }
        } catch (error) {
          console.error('[SYNC] Error parsing explore billboard data:', error);
        }
      }

      // 3. Check for package-specific localStorage keys
      const packageLocalStorageKeys = [
        'packageCart',
        'preLoginPackageData',
        'explorePackageArray',
        'packageBillboardCart',
        'dealCart'
      ];

      for (const key of packageLocalStorageKeys) {
        const packageData = localStorage.getItem(key);
        if (packageData) {
          try {
            const packageItems = JSON.parse(packageData);
            if (Array.isArray(packageItems) && packageItems.length > 0) {
              // Ensure package items have proper type identification
              const markedPackageItems = packageItems.map((item: any) => ({
                ...item,
                type: item.type || 'Package', // Ensure type is set
                isFromPackageStorage: true
              }));
              itemsToSync = [...itemsToSync, ...markedPackageItems];
            }
          } catch (error) {
            console.error(`[SYNC] Error parsing ${key}:`, error);
          }
        }
      }
      console.log('[COUNTRY CONFLICT] [SYNC] Total items collected for sync before filtering:', itemsToSync.length, itemsToSync);

      // ---------------------------------------------------------
      // 🛑 STRICT COUNTRY FILTERING (New Logic)
      // ---------------------------------------------------------
      // If a targetCountry is provided (e.g., from Keep Cart), remove ANY item that doesn't match.
      if (targetCountry) {
        const initialCount = itemsToSync.length;
        itemsToSync = itemsToSync.filter((item: any) => {
          console.log("[COUNTRY CONFLICT] item targetcountry---->", item);

          const itemCountry = item.countryName || item.cartItemCountry || item.billboardCountry || item.country || '';
          console.log("[COUNTRY CONFLICT] itemcountry---------->", itemCountry);

          // If item has no country, keep it (risky but safer). If it has country, it MUST match target.
          if (!itemCountry) return true;
          return itemCountry.toLowerCase() === targetCountry.toLowerCase();
        });
        console.log(`[COUNTRY CONFLICT] [SYNC] Filtered items by target country (${targetCountry}): ${initialCount} -> ${itemsToSync.length}`);
      }
      // ---------------------------------------------------------

      // ---------------------------------------------------------
      // 🛑 DEDUPLICATION (New Logic)
      // ---------------------------------------------------------
      // Deduplicate the gathered list itself (in case same item is in multiple storage keys)
      const uniqueSyncIds = new Set();
      itemsToSync = itemsToSync.filter(item => {
        const id = getNormalizedId(item);
        if (uniqueSyncIds.has(id)) return false;
        uniqueSyncIds.add(id);
        return true;
      });
      // ---------------------------------------------------------

      // If no items to sync, mark as completed and return
      if (itemsToSync.length === 0) {
        // Clean up localStorage (including package-specific keys)
        localStorage.removeItem('preLoginExploreFlowData');
        localStorage.removeItem('exploreBillboardArray');
        localStorage.removeItem('billboardCart');
        localStorage.removeItem('preLoginCartData');
        localStorage.removeItem('packageCart');
        localStorage.removeItem('preLoginPackageData');
        localStorage.removeItem('explorePackageArray');
        localStorage.removeItem('packageBillboardCart');
        localStorage.removeItem('dealCart');
        setHasLocalStorageSynced(true);
        setHasProcessedExploreData(true);
        sessionStorage.setItem('cartSyncCompleted', 'true');
        setIsSyncing(false);
        return;
      }

      if (existingCartItems.length > 0 && itemsToSync.length > 0) {
        const serverCountryCheck = getCartCountry(existingCartItems);
        const localCountryCheck = getCartCountry(itemsToSync); // Uses the context helper

        console.log(`[COUNTRY CONFLICT] [SYNC CHECK] Server: ${serverCountryCheck}, Local: ${localCountryCheck}`);

        // Normalize strings for comparison
        const normalize = (str: string) => str ? str.toLowerCase().trim() : '';

        // If both exist and are different
        if (serverCountryCheck && localCountryCheck && normalize(serverCountryCheck) !== normalize(localCountryCheck) && !ignoreConflictState) {
          console.warn(`[COUNTRY CONFLICT] [SYNC BLOCKED] Conflict detected. Server: ${serverCountryCheck}, Local: ${localCountryCheck}`);

          // 1. Update State for Modal
          setServerCartCountry(serverCountryCheck);
          setLocalCartCountry(localCountryCheck);
          setHasCountryConflict(true);
          setShowCountryConflictModal(true);

          // 2. STOP EXECUTION IMMEDIATELY
          setIsSyncing(false);
          return;
        }
      }

      // Filter out items that already exist in the cart (deduplication against API data)
      const filteredItems = itemsToSync.filter((item: any) => {
        const itemId = getNormalizedId(item);
        const isDuplicate = existingCartIds.has(itemId);
        return !isDuplicate;
      });
      // Deduplicate within the filtered items themselves by normalized ID
      const uniqueItems = [];
      const seenIds = new Set();
      for (const item of filteredItems) {
        const id = getNormalizedId(item);
        if (!seenIds.has(id)) {
          uniqueItems.push(item);
          seenIds.add(id);
        }
      }

      // If no unique items to add, clean up and return
      if (uniqueItems.length === 0) {
        // Clean up localStorage (including package-specific keys)
        localStorage.removeItem('preLoginExploreFlowData');
        localStorage.removeItem('exploreBillboardArray');
        localStorage.removeItem('billboardCart');
        localStorage.removeItem('preLoginCartData');
        localStorage.removeItem('packageCart');
        localStorage.removeItem('preLoginPackageData');
        localStorage.removeItem('explorePackageArray');
        localStorage.removeItem('packageBillboardCart');
        localStorage.removeItem('dealCart');
        setHasLocalStorageSynced(true);
        setHasProcessedExploreData(true);
        sessionStorage.setItem('cartSyncCompleted', 'true');
        setIsSyncing(false);
        return;
      }

      // Prepare API payload
      const today = new Date();
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const generatedCampaignName = `Campaign_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}_${Math.random().toString(36).substring(7)}`;

      const safeFormatDate = (date: Date, formatStr: string): string => {
        try {
          return format(date, formatStr);
        } catch (error) {
          console.error('[SYNC] Error formatting date:', error);
          return '';
        }
      };

      // Use dates from context for API sync
      const syncStartDate = startDate || today;
      const syncEndDate = endDate || today;

      const formattedSyncStartDate = safeFormatDate(syncStartDate, 'dd MMMM yyyy');
      const formattedSyncEndDate = safeFormatDate(syncEndDate, 'dd MMMM yyyy');
      const isoSyncStartDate = safeFormatDate(syncStartDate, 'yyyy-MM-dd');
      const isoSyncEndDate = safeFormatDate(syncEndDate, 'yyyy-MM-dd');

      // Enhanced package detection - check multiple possible type indicators
      const packages = uniqueItems.filter((item: any) => {
        return item.type === 'Package' ||
          item.type === 'package' ||
          item.billboardType === 'Package' ||
          item.billboardType === 'package' ||
          item.isDeal === true ||
          item.inventoryType === 'Package' ||
          item.inventoryType === 'package' ||
          (item.packageDetails && typeof item.packageDetails === 'object') ||
          (item.validityStartDate && item.validityEndDate); // Package indicators
      });

      const regularBillboards = uniqueItems.filter((item: any) => {
        return !(item.type === 'Package' ||
          item.type === 'package' ||
          item.billboardType === 'Package' ||
          item.billboardType === 'package' ||
          item.isDeal === true ||
          item.inventoryType === 'Package' ||
          item.inventoryType === 'package' ||
          (item.packageDetails && typeof item.packageDetails === 'object') ||
          (item.validityStartDate && item.validityEndDate));
      });

      let syncSuccessCount = 0;
      let totalItemsToSync = uniqueItems.length;


      let campaignIdFromSync: string | null = null;

      // Handle regular billboards with ADD_TO_CART_SUBMIT
      if (regularBillboards.length > 0) {
        const formattedBillboards = regularBillboards.map((item: any) => ({
          accessToken: token,
          userId: userId,
          campaignName: generatedCampaignName,
          startDate: isoSyncStartDate,
          endDate: isoSyncEndDate,
          cartItemName: item.name || item.billboardName || item.displayName || 'Billboard',
          cartItemId: getNormalizedId(item),
          cartItemReferenceId: item.referenceId || item.billboardReferenceId || '',
          cartItemType: item.type || item.billboardType || 'Digital',
          // ✅ FIX: Include item.countryName in the fallback chain
          cartItemCountry: item.country || item.billboardCountry || item.countryName || cartCountry || '',
          cartItemVenueType: item.venueType || item.billboardVenueType || '',
          cartItemResolution: item.size || item.billboardSize || '1920x1080',
          cartItemThumbnailUrl: item.image || item.billboardThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          cartItemLatitude: parseFloat(item.latitude || item.billboardLatitude || 0),
          cartItemLongitude: parseFloat(item.longitude || item.billboardLongitude || 0),
          // cartItemCurrencyCode: item.currencyCode || localStorage.getItem('currencyCode') || '',
          packageDetails: '',
          // priceFormat: priceFormat || 'N/A',
          isSpotBased: item?.isSpotBased,
        }));


        try {
          const billboardResponse = await client.mutate({
            mutation: ADD_TO_CART_SUBMIT,
            variables: { input: formattedBillboards },
            fetchPolicy: 'no-cache'
          });

          if (billboardResponse.data?.addtocart?.itemData[0]?.success) {
            syncSuccessCount += formattedBillboards.length;

            // Capture campaignId from the response
            if (billboardResponse.data?.addtocart?.cartItems?.[0]?.id && !campaignIdFromSync) {
              campaignIdFromSync = billboardResponse.data.addtocart.cartItems[0].id;
            }
          } else {
            console.error('[SYNC] Failed to sync regular billboards:', billboardResponse.data?.addtocart?.itemData?.message);
          }
        } catch (error) {
          console.error('[SYNC] Error syncing regular billboards:', error);
        }
      }

      // Handle packages with ADD_CAMPAIGN_CART
      if (packages.length > 0) {

        // Helper to remove __typename recursively
        const removeTypename = (obj: any): any => {
          if (Array.isArray(obj)) return obj.map(removeTypename);
          if (obj && typeof obj === 'object') {
            const newObj: any = {};
            for (const key in obj) {
              if (key !== '__typename') {
                newObj[key] = removeTypename(obj[key]);
              }
            }
            return newObj;
          }
          return obj;
        };

        // Process each package for ADD_CAMPAIGN_CART
        for (const item of packages) {
          // Enhanced package data extraction
          const pkg = item.packageDetails || item; // Use stored packageDetails or fallback to item itself

          // Handle different package data structures
          const packageName = pkg.name || item.name || item.billboardName || '';
          const packageId = pkg.id || item.id || item.billboardId || item.inventoryId || '';
          const packageThumbnail = pkg.thumbnail || item.thumbnail || item.image || item.billboardThumbnailUrl || '';
          const packageCountry = pkg.country?.name || item.country?.name || item.billboardCountry || '';
          const packagePrice = pkg.price || item.price || item.totalPrice || 0;
          const packageTotalPrice = pkg.totalPrice || item.totalPrice || pkg.price || item.price || 0;

          console.log(`[SYNC] Processing package:`, {
            packageName,
            packageId,
            packagePrice,
            originalItem: item,
            extractedPkg: pkg
          });

          try {
            // --- FIX: Use the package's own VALIDITY dates for the top-level start/end date ---
            // The API is using these top-level dates as the validity dates, not the campaign dates.
            const packageStartDate = item.packageStartDate || pkg.validityStartDate || isoSyncStartDate;
            const packageEndDate = item.packageEndDate || pkg.validityEndDate || isoSyncEndDate;

            const packageInput = [{
              accessToken: token,
              userId: userId,
              campaignName: generatedCampaignName,
              startDate: safeFormatDate(new Date(packageStartDate), 'yyyy-MM-dd'),
              endDate: safeFormatDate(new Date(packageEndDate), 'yyyy-MM-dd'),
              cartItemName: packageName,
              cartItemId: packageId,
              cartItemReferenceId: pkg.referenceId || item.referenceId || null,
              cartItemType: 'PACKAGE',
              cartItemCountry: packageCountry,
              cartItemVenueType: pkg.venueType || item.venueType || null,
              cartItemResolution: pkg.resolution || item.resolution || '',
              cartItemThumbnailUrl: packageThumbnail,
              cartItemLatitude: pkg.latitude || item.latitude || null,
              cartItemLongitude: pkg.longitude || item.longitude || null,
              packageDetails: {
                ...removeTypename({
                  id: pkg?.id || item.id || '',
                  name: pkg?.name || item.name || '',
                  description: pkg?.description || '',
                  country: pkg?.country ? {
                    id: pkg.country.id || '',
                    name: pkg.country.name || '',
                    timezone: pkg.country.timezone || ''
                  } : null,
                  publisher: pkg?.publisher ? {
                    id: pkg.publisher.id || '',
                    name: pkg.publisher.name || ''
                  } : null,
                  startDate: pkg?.startDate || item.packageStartDate || '',
                  endDate: pkg?.endDate || item.packageEndDate || '',
                  validityStartDate: pkg?.validityStartDate || item.packageStartDate || '',
                  validityEndDate: pkg?.validityEndDate || item.packageEndDate || '',
                  registryPackageType: pkg?.registryPackageType || '',
                  campaignDuration: pkg?.campaignDuration || item.campaignDuration || 0,
                  purchaseLimitation: pkg?.purchaseLimitation || 0,
                  numberOfPurchase: pkg?.numberOfPurchase || 0,
                  inventory: pkg?.inventory || {
                    type: [],
                    total: 0,
                    classic: 0,
                    digital: 0
                  },
                  exposure: pkg?.exposure || [],
                  currency: pkg?.currency || item.currencyCode || '',
                  price: pkg?.price || item.price / 100 || 0, // Convert back from cents if needed
                  discount: pkg?.discount || 0,
                  totalPrice: pkg?.totalPrice || item.price / 100 || 0,
                  thumbnail: pkg?.thumbnail || item.image || '',
                  estimation: pkg?.estimation || {
                    potentialViews: item.impressions || 0,
                    uniqueReach: 0,
                    avgFrequency: 0,
                    cpm: 0,
                    spotsPerHour: 0,
                    totalSpots: 0
                  },
                  inventories: pkg?.inventories || [],
                  publishTo: pkg?.publishTo || [],
                  tagIds: [],
                  tags: pkg?.tags || [],
                  reopened: pkg?.reopened || false,
                  createdBy: pkg?.createdBy || '',
                  createdDate: pkg?.createdDate || '',
                  lastModifiedBy: pkg?.lastModifiedBy || '',
                  lastModifiedDate: pkg?.lastModifiedDate || '',
                  discountPrice: pkg?.discountPrice || {
                    originalPrice: 0,
                    totalPrice: 0,
                    discountPrice: 0
                  },
                  loadingicon: pkg?.loadingicon || false,
                  cartAdded: pkg?.cartAdded || false,
                  displayCount: pkg?.displayCount || 0,
                  showlocation: pkg?.showlocation || false,
                  packageexpire: pkg?.packageexpire || 0,
                  packagescreens: pkg?.packagescreens || 0,
                  packagediscountPrice: pkg?.packagediscountPrice || 0,
                  type: 'PACKAGE'
                })
              },
              discount: pkg.discount ? (packagePrice * pkg.discount) / 100 : 0,
              nowPayPrice: packageTotalPrice,
              totalPrice: packagePrice,
              spotDuration: pkg.spotDuration || item.spotDuration || null
            }];

            const packageResponse = await client.mutate({
              mutation: ADD_CAMPAIGN_CART,
              variables: { input: packageInput },
              fetchPolicy: 'no-cache'
            });

            if (packageResponse.data?.addCampaignCart?.itemData[0]?.success) {
              syncSuccessCount++;
              console.log(`[SYNC] Package ${pkg.name} synced successfully`);

              // Capture campaignId from the response if not already captured
              if (packageResponse.data?.addCampaignCart?.itemData[0]?.id && !campaignIdFromSync) {
                campaignIdFromSync = packageResponse.data.addCampaignCart.itemData[0].id;
                console.log(`[SYNC] Captured campaignId from package sync: ${campaignIdFromSync}`);
              }

              // Call fetchCartList() after successful package sync
              try {
                await fetchCartList();
                console.log(`[SYNC] Cart data refreshed after package ${pkg.name} sync`);
              } catch (fetchError) {
                console.error(`[SYNC] Error refreshing cart data after package sync:`, fetchError);
              }
            } else {
              console.error(`[SYNC] Failed to sync package ${pkg.name}:`, packageResponse.data?.addCampaignCart?.itemData?.message);
            }
          } catch (error) {
            console.error(`[SYNC] Error syncing package ${pkg.name}:`, error);
          }
        }
      }

      // Check overall success
      if (syncSuccessCount === totalItemsToSync) {

        // Set the campaignId if we captured it from the sync response
        if (campaignIdFromSync) {
          setCampaignId(campaignIdFromSync);
          localStorage.setItem('selectedCampaignId', campaignIdFromSync);
        }

        // Immediate UI update - fetch fresh cart data only once
        try {
          await fetchCartList();

          // Force component re-render
          window.dispatchEvent(new Event('cartUpdated'));
        } catch (fetchError) {
          console.error('❌ Error fetching updated cart data after sync:', fetchError);
        }

        // Clean up localStorage after successful addition (including package-specific keys)
        localStorage.removeItem('preLoginExploreFlowData');
        localStorage.removeItem('exploreBillboardArray');
        localStorage.removeItem('billboardCart');
        localStorage.removeItem('preLoginCartData');

        // Clean up package-specific localStorage keys
        localStorage.removeItem('packageCart');
        localStorage.removeItem('preLoginPackageData');
        localStorage.removeItem('explorePackageArray');
        localStorage.removeItem('packageBillboardCart');
        localStorage.removeItem('dealCart');

        // Mark sync and processing as completed
        setHasLocalStorageSynced(true);
        setHasProcessedExploreData(true);
        sessionStorage.setItem('cartSyncCompleted', 'true');

        // Refresh cart data - only call fetchCartList, it will update everything
        await fetchCartList();

        // Show success toast
        toast({
          title: "Success",
          description: `${totalItemsToSync} unique item(s) added to cart.`,
        });
      } else {
        console.error(`[SYNC] Only ${syncSuccessCount} of ${totalItemsToSync} items synced successfully`);
        setHasLocalStorageSynced(false);
        sessionStorage.removeItem('cartSyncCompleted');
        toast({
          title: "Error",
          description: "Some items failed to sync to your cart",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[SYNC] Error syncing localStorage cart to API:', error);
      setHasLocalStorageSynced(false);
      sessionStorage.removeItem('cartSyncCompleted');
      toast({
        title: "Error",
        description: "Failed to sync cart items. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCheckboxClick = () => {
    setShowTerms(true); // Open the modal
  };
  const handleSuccessClose = () => {
    setShowSuccessModal(false);

    // Clear general campaign data from localStorage
    localStorage.removeItem('campaignCart');
    localStorage.removeItem('currentCampaign');

    // Clear campaign-specific data if we have a campaign ID
    if (campaignId) {
      // Clear all campaign-specific keys
      localStorage.removeItem(`campaign_${campaignId}_name`);
      localStorage.removeItem(`campaign_${campaignId}_startDate`);
      localStorage.removeItem(`campaign_${campaignId}_endDate`);
      localStorage.removeItem('campaignName');
      localStorage.removeItem('startDate');
      localStorage.removeItem('endDate');

      // Also clear any other campaign-related data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(`campaign_${campaignId}`)) {
          keysToRemove.push(key);
        }
      }

      // Remove all identified keys
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }

    // Also remove the legacy keys for backward compatibility
    localStorage.removeItem(`campaign_${campaignId}_name`);
    localStorage.removeItem(`campaign_${campaignId}_startDate`);
    localStorage.removeItem(`campaign_${campaignId}_endDate`);
    localStorage.removeItem('campaignName');
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('selectedCampaignId');

    // Don't navigate away - stay on the current page
  };

  // // If no campaign data, but we have cart items, create default campaign
  // if (!campaignData && cartItems.length > 0) {
  //   const defaultCampaign = {
  //     name: 'New Campaign',
  //     startDate: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
  //     endDate: new Date(new Date().setDate(new Date().getDate() + 30)).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
  //     state: 'Queensland',
  //     district: 'Brisbane'
  //   };
  //   setCampaignData(defaultCampaign);
  //   localStorage.setItem('currentCampaign', JSON.stringify(defaultCampaign));
  // }

  // Calculate campaign duration
  const calculateDuration = (start: string, end: string) => {
    if (!start || !end) return "";

    const startDate = new Date(start);
    const endDate = new Date(end);

    // Use date-fns for more accurate calendar day calculation
    // Add 1 to include both start and end dates (inclusive)
    const diffDays = differenceInCalendarDays(endDate, startDate) + 1;

    // Always show days for clarity, especially for packages
    return `${diffDays} days`;
  };

  // Local helper to get automatic campaign dates from actual displayed items
  const getLocalAutomaticCampaignDates = () => {
    // Use the same logic as itemsToDisplay to get the correct data source
    const itemsToDisplay = isLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems;

    const packagesAndDeals = itemsToDisplay.filter((item: any) =>
      (item.billboardType === "Package" || item.type === "Package") ||
      (item.billboardType === "Deal" || item.type === "Deal") ||
      item.isDeal
    );

    if (packagesAndDeals.length === 0) return null;

    // For non-authenticated users, use current date as start and end (one day campaign)
    if (!isLoggedIn) {
      const today = new Date();

      return {
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    }

    // Find the latest start date and end date among all packages/deals
    let latestStartDate = "";
    let latestEndDate = "";

    packagesAndDeals.forEach((item: any) => {
      let startDate = "";
      let endDate = "";

      if (isLoggedIn && item.validityStartDate && item.validityEndDate) {
        // For API data, use the real validity dates
        startDate = item.validityStartDate;
        endDate = item.validityEndDate;
      } else {
        // For local cart data, extract from package properties (fallback to context function)
        const props = getInventoryTypeProperties(item.billboardType || item.type, item);
        startDate = props.packageStartDate || "";
        endDate = props.packageEndDate || "";
      }

      if (startDate && (!latestStartDate || startDate > latestStartDate)) {
        latestStartDate = startDate;
      }
      if (endDate && (!latestEndDate || endDate > latestEndDate)) {
        latestEndDate = endDate;
      }
    });

    return latestStartDate && latestEndDate ?
      { startDate: latestStartDate, endDate: latestEndDate } : null;
  };

  // Helper function to check if a package is available based on validity dates vs campaign dates
  // Memoized function to check if a package is available based on validity dates vs campaign dates
  const isPackageAvailable = useCallback((item: any) => {
    // For packages, check validity dates against campaign dates
    if ((item.billboardType?.toLowerCase() === "package" || item.type?.toLowerCase() === "package") || item.isDeal) {
      // Get campaign dates
      if (!startDate || !endDate) {
        return false;
      }

      // Get package validity dates
      const packageStartDate = item.validityStartDate || item.packageStartDate;
      const packageEndDate = item.validityEndDate || item.packageEndDate;

      // Get campaign dates from context
      const campaignStartDateFromContext = startDate;
      const campaignEndDateFromContext = endDate;

      if (!packageStartDate || !packageEndDate || !campaignStartDateFromContext || !campaignEndDateFromContext) {
        return false; // Not enough data to validate
      }

      // Convert to Date objects for comparison (normalize to start of day)
      const campaignStart = new Date(campaignStartDateFromContext);
      const campaignEnd = new Date(campaignEndDateFromContext);
      const packageStart = new Date(packageStartDate);
      const packageEnd = new Date(packageEndDate);

      // Set all times to start of day for date-only comparison
      campaignStart.setHours(0, 0, 0, 0);
      campaignEnd.setHours(0, 0, 0, 0);
      packageStart.setHours(0, 0, 0, 0);
      packageEnd.setHours(0, 0, 0, 0);

      // Validation Rules
      // 1. Minimum buy days
      // 2. Maximum buy days
      // 3. Campaign must be fully inside package validity

      // Get minimum buy days
      const minDays = item.minimumSellingDays || item.campaignDuration || 1;

      // Calculate campaign days
      const campaignDays = Math.floor((campaignEnd.getTime() - campaignStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      // Calculate package validity days
      const packageValidityDays = Math.floor((packageEnd.getTime() - packageStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      // Rule 1: Minimum buy days
      const minDaysValid = campaignDays >= minDays;
      // Rule 2: Maximum buy days
      const maxDaysValid = campaignDays <= packageValidityDays;
      // Rule 3: Campaign window constraint
      const windowValid = campaignStart >= packageStart && campaignEnd <= packageEnd;

      return minDaysValid && maxDaysValid && windowValid;
    }

    // For digital/classic items, use existing availableBooking property
    return item.availableBooking !== false;
  }, [startDate, endDate]);

  // Calculate duration inside a state to ensure it updates
  const [campaignDuration, setCampaignDuration] = useState<number>(0);
  const [priceUpdateTrigger, setPriceUpdateTrigger] = useState<number>(0);

  useEffect(() => {
    // Check if both startDate and endDate are not null
    if (startDate && endDate) {
      const calculatedLength = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1;
      setCampaignDuration(calculatedLength);
      // Trigger price updates
      setPriceUpdateTrigger(prev => prev + 1);
    } else {
      // Handle the case where either date is null
      setCampaignDuration(0);
    }
  }, [startDate, endDate]);

  // const campaignDuration = calculateDuration(startDate, endDate);

  // Get campaign duration in days for pricing calculations
  const getCampaignDurationDays = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 30;
    }
    return 30; // Default campaign duration
  };

  // Check for Classic Billboards 30-day minimum validation
  const validateClassicBillboardDuration = (days: number) => {
    const hasClassicBillboards = cartItems.some(item => item.type === "Classic" && !item.isDeal);
    // if (hasClassicBillboards && days < 30) {
    //   setDurationError("Classic Billboards require a minimum booking duration of 30 days.");
    //   return false;
    // }
    setDurationError("");
    return true;
  };

  // Check for draft campaign data in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const draftId = urlParams.get('draftId');
    const draftName = urlParams.get('draftName') || urlParams.get('campaignName') || 'New campaign';
    const draftStartDate = urlParams.get('startDate');
    const draftEndDate = urlParams.get('endDate');
    const editFlag = urlParams.get('edit') === 'true';
    const sourceParam = urlParams.get('source') || '';


    if (draftId) {
      setDraftCampaignId(draftId);
      setCampaignId(draftId);
      setCampaignName(decodeURIComponent(draftName));
      if (draftStartDate) updateStartDate(new Date(draftStartDate));
      if (draftEndDate) updateEndDate(new Date(draftEndDate));
      setEdit(editFlag);
      setSource(sourceParam);


      // Clear URL parameters after loading
      // const newUrl = window.location.pathname;
      // window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    // Only fetch initial cart data when campaignId and edit are set (for draft flow)
    if (isLoggedIn && campaignId && typeof edit === 'boolean') {
      fetchInitialCartData();
    }
  }, [isLoggedIn, campaignId, edit]);

  // Auto-set dates for packages/deals when no manual dates required
  useEffect(() => {
    const automaticDates = getLocalAutomaticCampaignDates();
    const needsManualDates = requiresCampaignDates();

    // If no manual dates are required and we have automatic dates, use them
    // Only set if dates are actually different to prevent loops
    if (!needsManualDates && automaticDates && !startDate && !endDate) {
      const newStartDate = new Date(automaticDates.startDate);
      const newEndDate = new Date(automaticDates.endDate);

      // Only update if dates are null/undefined
      if (!startDate || !endDate) {
        updateStartDate(newStartDate);
        updateEndDate(newEndDate);
      }
    }
  }, [cartItems, startDate, endDate]); // Added startDate, endDate to dependencies

  // Update campaign durations for all cart items when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const days = getCampaignDurationDays();
      // Validate duration for Classic billboards only if they exist
      const breakdown = getInventoryTypeBreakdown();
      if (breakdown.classic > 0) {
        validateClassicBillboardDuration(days);
      } else {
        setDurationError(""); // Clear any existing errors
      }

      // Update all cart items with the new duration (only for Digital/Classic)
      cartItems.forEach(item => {
        if (typeof item.id === 'number' && (item.type?.toLowerCase() === 'digital' || item.type?.toLowerCase() === 'classic')) {
          updateCampaignDuration(item.id, days);
        }
      });
    }
  }, [startDate, endDate]);

  // Update campaign duration in cart context when campaign duration changes (for non-logged-in users)
  useEffect(() => {
    if (!isLoggedIn && campaignDuration > 0) {
      setCurrentCampaignDuration(campaignDuration);
    } else if (isLoggedIn) {
      setCurrentCampaignDuration(null);
    }
  }, [campaignDuration, isLoggedIn, setCurrentCampaignDuration]);

  // Use totalCost from cart context which now automatically uses currentCampaignDuration
  const currentTotalCost = totalCost;

  // Memoized function to calculate individual billboard prices with current campaign duration
  const calculateBillboardPrice = useCallback((billboard: any) => {
    console.log("Calculating price for billboard:", billboard);

    if (isLoggedIn && billboard.billboardPeriodSubTotalPrice !== undefined) {
      return formatPrice(
        billboard.billboardPeriodSubTotalPrice,
        billboard.billboardCurrencyCode || billboard.currencyCode || "N/A"
      );
    }

    const currentCampaignDuration = campaignDuration || 1;
    const billboardType = (billboard?.billboardType || billboard?.type || '').toLowerCase();

    if (billboard.isDeal || billboardType === "deal") {
      return formatPrice(billboard.price / 100, billboard.currencyCode || "N/A");
    }

    if (billboardType === "digital") {
      const isSpotBased = billboard?.isSpotBased || billboard?.priceFormat === 'spot-based';
      if (isSpotBased) {
        let currentSpots;
        if (isLoggedIn && billboard.cartItemId) {
          currentSpots = apiCartSpots[billboard.cartItemId] ||
            billboard.userSelectedSpots ||
            billboard.minimumSpotsPerDay || 1;
        } else {
          currentSpots = billboard.spots || 1;
        }

        return formatPrice(
          ((billboard?.price ?? 0) / 100 || billboard?.perSpotPrice || 0) *
          currentSpots * currentCampaignDuration,
          billboard.currencyCode || "N/A"
        );
      } else {
        const basePrice = billboard?.perSpotPrice || (billboard?.price / 100) || 0;
        return formatPrice(
          basePrice * currentCampaignDuration,
          billboard.currencyCode || "N/A"
        );
      }
    }

    if (billboardType === "classic") {
      return formatPrice(
        (billboard?.perDayPrice || 0) * currentCampaignDuration,
        billboard.currencyCode || "N/A"
      );
    }

    if (billboardType === "package") {
      const totalPrice = billboard.price / 100;
      const packageDuration = differenceInCalendarDays(
        new Date(billboard.packageEndDate),
        new Date(billboard.packageStartDate)
      ) + 1;
      const oneDayPrice = totalPrice / packageDuration;

      // For packages, show the total price for the current campaign duration
      // If the current campaign duration is within the package validity, multiply by campaign duration
      if (currentCampaignDuration && currentCampaignDuration <= packageDuration) {
        const finalPrice = oneDayPrice * currentCampaignDuration;
        return formatPrice(finalPrice, billboard.currencyCode || "N/A");
      } else {
        return formatPrice(totalPrice, billboard.currencyCode || "N/A");
      }
    }

    // Default case
    const basePrice = billboard?.perDayPrice || 0;
    return formatPrice(
      basePrice * currentCampaignDuration,
      billboard?.currencyCode || "N/A"
    );
  }, [campaignDuration, isLoggedIn, apiCartSpots, priceUpdateTrigger]);

  // Memoized function to calculate section totals with current campaign duration
  const calculateSectionTotal = useCallback((groupedBillboards: any, type: string) => {

    const total = groupedBillboards[type].reduce((sum: number, item: any) => {
      if (isLoggedIn && item.billboardPeriodSubTotalPrice !== undefined) {
        return sum + (item.billboardPeriodSubTotalPrice || 0);
      }

      const currentCampaignDuration = campaignDuration || 1;
      const itemType = (item?.billboardType || item?.type || '').toLowerCase();

      if (itemType === "digital") {
        const isSpotBased = item?.isSpotBased || item?.priceFormat === 'spot-based';
        if (isSpotBased) {
          return sum + (((item?.price ?? 0) / 100 || item?.perSpotPrice || 0) *
            (item?.spots || 1) * currentCampaignDuration);
        } else {
          const basePrice = item?.perSpotPrice || (item?.price / 100) || 0;
          return sum + (basePrice * currentCampaignDuration);
        }
      } else if (itemType === "classic") {
        return sum + ((item?.perDayPrice || 0) * currentCampaignDuration);
      } else if (item.isDeal || itemType === "deal") {
        return sum + ((item?.price || 0) / 100);
      } else if (itemType === "package") {
        const packageDuration = differenceInCalendarDays(
          new Date(item?.packageEndDate ?? ""),
          new Date(item?.packageStartDate ?? "")
        ) + 1;
        const price = (item?.price || 0) / 100;
        const oneDayPrice = price / packageDuration;

        // For packages, calculate based on current campaign duration
        if (currentCampaignDuration && currentCampaignDuration <= packageDuration) {
          return sum + (oneDayPrice * currentCampaignDuration);
        } else {
          // Use full package price if campaign duration exceeds package validity
          return sum + price;
        }
      } else {
        const basePrice = ((item?.pre || item?.price) / 100) || 0;
        return sum + (basePrice * currentCampaignDuration);
      }
    }, 0);

    const firstItem = groupedBillboards[type][0];
    const itemCurrencyCode = firstItem?.billboardCurrencyCode || firstItem?.currencyCode || billboardCurrencyCode || currencyCode;
    return formatPrice(total, itemCurrencyCode);
  }, [campaignDuration, isLoggedIn, billboardCurrencyCode, currencyCode, priceUpdateTrigger]);

  // Lead form states (for non-authenticated users)
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;
  const countryId = localStorage.getItem('selectedCountryId') || localStorage.getItem('sessionDefaultCountryId');
  
  // Fetch country codes from API
  const { data: countryCodeData, loading: countryCodeLoading } = useQuery(COUNTRY_CODE_LIST);
  const countryCodeOptions = countryCodeData?.countryCodeList || [];

  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquiryCountryCode, setEnquiryCountryCode] = useState("+1");
  const [enquiryCountryName, setEnquiryCountryName] = useState("United States");
  const [enquiryCountrySearchQuery, setEnquiryCountrySearchQuery] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    campaignStartDate: startDate ? format(startDate, 'yyyy-MM-dd') : "",
    campaignEndDate: endDate ? format(endDate, 'yyyy-MM-dd') : ""
  });

  // Validation patterns
  const VALIDATION_PATTERNS = {
    fullName: /^[A-Za-z ]{3,50}$/,
    email: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    phone: /^[0-9]{7,15}$/,
    company: /^[A-Za-z0-9 .&\-]{2,100}$/,
    address: /^[A-Za-z0-9 ,./\-]{5,200}$/
  };

  // Validation error messages state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Validation function for individual fields
  const validateField = (fieldName: string, value: string): string => {
    if (!value.trim()) {
      if (fieldName === 'company' || fieldName === 'address') {
        return ''; // Optional fields
      }
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    switch (fieldName) {
      case 'name':
        if (value.length < 3) return 'Full Name must be at least 3 characters';
        if (value.length > 50) return 'Full Name must not exceed 50 characters';
        if (!VALIDATION_PATTERNS.fullName.test(value)) {
          return 'Full Name can only contain alphabets and spaces';
        }
        break;
      case 'email':
        if (value.length < 5) return 'Email must be at least 5 characters';
        if (value.length > 100) return 'Email must not exceed 100 characters';
        if (!VALIDATION_PATTERNS.email.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!VALIDATION_PATTERNS.phone.test(value)) {
          return 'Phone number must contain only digits (7-15 digits)';
        }
        break;
      case 'company':
        if (value && value.length < 2) return 'Company must be at least 2 characters';
        if (value && value.length > 100) return 'Company must not exceed 100 characters';
        if (value && !VALIDATION_PATTERNS.company.test(value)) {
          return 'Company can only contain alphabets, numbers, spaces, and symbols (.-&)';
        }
        break;
      case 'address':
        if (value && value.length < 5) return 'Address must be at least 5 characters';
        if (value && value.length > 200) return 'Address must not exceed 200 characters';
        if (value && !VALIDATION_PATTERNS.address.test(value)) {
          return 'Address can only contain alphabets, numbers, comma, dot, slash, and hyphen';
        }
        break;
    }
    return '';
  };

  // Function to validate entire form
  const validateEnquiryForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    const nameError = validateField('name', enquiryForm.name);
    if (nameError) errors.name = nameError;

    const emailError = validateField('email', enquiryForm.email);
    if (emailError) errors.email = emailError;

    const phoneError = validateField('phone', enquiryForm.phone);
    if (phoneError) errors.phone = phoneError;

    const companyError = validateField('company', enquiryForm.company);
    if (companyError) errors.company = companyError;

    const addressError = validateField('address', enquiryForm.address);
    if (addressError) errors.address = addressError;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Click outside handler for country dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown]);

  // Campaign submission for authenticated users using GraphQL
  // const submitCampaignMutation = useMutation({
  //   mutationFn: async (campaignData: any) => {
  //     const token = localStorage.getItem("authToken");

  //     if (!token || !campaignId) {
  //       throw new Error("Authentication token or campaign ID not found");
  //     }

  //     // Prepare variables for REQUEST_FOR_APPROVAL mutation
  //     const approvalVariables = {
  //       campaignType: "REQUESTED", // Request for approval
  //       campaignId: campaignId,
  //       input: {
  //         userId: localStorage.getItem("userId"),
  //         accessToken: token,
  //         campaignName: campaignData.name,
  //         startDate: format(campaignData.startDate, 'dd MMM yyyy'),
  //         endDate: format(campaignData.endDate, 'dd MMM yyyy'),
  //         campaignBudget: campaignData.totalBudget,
  //         campaignNote: campaignData.campaignNotes,
  //         selectedBillboards: (isLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems).map((inventory: any) => ({
  //           inventoryId: inventory.billboardId || inventory.id,
  //           inventoryName: inventory.billboardName || inventory.name,
  //           inventoryType: inventory.billboardType || inventory.type,
  //           inventoryAddress: inventory.billboardLocation || inventory.location,
  //           inventoryVenueType: inventory.billboardVenueType || "outdoor",
  //           inventoryResolution: {
  //             width: parseFloat(inventory.billboardResolutionWidth || "1920"),
  //             height: parseFloat(inventory.billboardResolutionHeight || "1080")
  //           },
  //           inventoryThumbnailUrl: inventory.billboardThumbnailUrl || inventory.image,
  //           inventoryLatitude: inventory.billboardLatitude || 0,
  //           inventoryLongitude: inventory.billboardLongitude || 0,
  //           inventoryReports: {
  //             totalPotentialViews: inventory.billboardPotentialViews || inventory.impressions || 0,
  //             averageFrequency: inventory.billboardFrequency || 1,
  //             cpm: inventory.billboardPeriodSubTotalPrice ?
  //               (inventory.billboardPeriodSubTotalPrice / ((inventory.billboardPotentialViews || 0) / 1000)) :
  //               ((inventory.price || 0) / 100),
  //             totalReach: inventory.billboardUniqueReach || inventory.impressions || 0,
  //           },
  //         }))
  //       },
  //     };

  //     const response = await client.mutate({
  //       mutation: REQUEST_FOR_APPROVAL,
  //       variables: approvalVariables,
  //       fetchPolicy: "no-cache",
  //     });

  //     return response.data;
  //   },
  //   onSuccess: async (data) => {
  //     console.log("✅ submitCampaignMutation.onSuccess called with data:", data);
  //     if (data?.requestForApproval?.campaign?.campaignStatus === "REQUESTED") {
  //       console.log("✅ Campaign status is REQUESTED, proceeding with success flow");
  //       setShowSuccess(true);
  //       clearCart();
  //       console.log("✅ Cart cleared, now fetching cart count...");

  //       // Fetch updated cart count from API
  //       await fetchCartCountDetails();

  //       // Clean up localStorage after successful request
  //       localStorage.removeItem(`campaign_${campaignId}_name`);
  //       localStorage.removeItem(`campaign_${campaignId}_startDate`);
  //       localStorage.removeItem(`campaign_${campaignId}_endDate`);
  //       localStorage.removeItem('campaignName');
  //       localStorage.removeItem('startDate');
  //       localStorage.removeItem('endDate');
  //       localStorage.removeItem('selectedCampaignId');

  //       toast({
  //         title: "Success",
  //         description: "Campaign submitted for approval successfully!",
  //         variant: "default"
  //       });
  //     }
  //   },
  //   onError: (error: any) => {
  //     console.error("Campaign submission error:", error);
  //     toast({
  //       title: "Error",
  //       description: "Failed to submit campaign. Please try again.",
  //       variant: "destructive"
  //     });
  //   },
  // });

  // Lead form submission for non-authenticated users



  const [submitEnquiryMutation] = useMutation(SUBMIT_ENQUIRY);

  // Error and success handlers for enquiry submission
  const handleEnquiryError = (err: any) => {
    console.error('Error submitting enquiry:', err);
    let errorMessage = 'An unexpected error occurred. Please try again later.';
    if ('networkError' in err && err.networkError) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if ('graphQLErrors' in err && Array.isArray((err as any).graphQLErrors) && (err as any).graphQLErrors.length > 0) {
      errorMessage = (err as any).graphQLErrors[0].message || errorMessage;
    }
    setSubmissionError(errorMessage);
    setIsSubmitting(false);
  };

  const handleEnquirySuccess = (data: any) => {
    if (data.submitEnquiry.message) {
      setEnquiryForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        campaignStartDate: startDate ? format(startDate, 'yyyy-MM-dd') : "",
        campaignEndDate: endDate ? format(endDate, 'yyyy-MM-dd') : ""
      });
      updateStartDate(today);
      updateEndDate(today);
      setIsSubmitting(false);
      setShowSuccessModal(true);
      setShowEnquiryForm(false); // <-- close the enquiry form/modal
      clearCart(); // <-- clear the cart data
    }
    // toast.success(data.submitEnquiry.message || 'Enquiry submitted successfully!');
  };

  const handleSubmitEnquiry = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate form before submission
    if (!validateEnquiryForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form and try again.",
        variant: "destructive"
      });
      return;
    }

    console.log("start date-->", startDate, "end date-->", endDate);
    const storedBillboardIds = localStorage.getItem('addedBillboardIds');
    let parsedBillboardIds: string[] = [];
    if (storedBillboardIds) {
      try {
        parsedBillboardIds = JSON.parse(storedBillboardIds);
      } catch (error) {
        console.error('Error parsing addedBillboardIds:', error);
        // toast.error('Failed to load billboard data');
        setIsSubmitting(false);
        return;
      }
    }

    // You may want to get cart items from cartList or cartItems
    const billboardSource = cartList && cartList.length > 0 ? cartList : cartItems;
    const formattedBillboardIds: AddedBillboardInput[] = billboardSource.map(item => {
      console.log("item check in submit enquiry-->", item);

      // For API cart items (cartList) - typically from logged-in users
      if ('billboardId' in item && 'billboardType' in item) {
        return {
          billboardId: item.billboardId,
          billboardType: String(item.billboardType)?.toLowerCase()
        };
      }

      // For local cart items (cartItems) - typically from non-logged-in users
      // Handle packages, digital, classic, deals, etc.
      const itemType = (item as any).type || 'unknown';
      return {
        billboardId: (item as any).id || '',
        billboardType: String(itemType)?.toLowerCase() // This will convert 'Package' -> 'package', 'Digital' -> 'digital', etc.
      };
    });

    if (formattedBillboardIds.length === 0 && parsedBillboardIds.length > 0) {
      formattedBillboardIds.push(...parsedBillboardIds.map(id => ({
        billboardId: id,
        billboardType: 'Unknown'
      })));
    }

    if (!enquiryForm.campaignStartDate || !enquiryForm.campaignEndDate) {
      // setErrors((prev) => ({ ...prev, dates: 'Start and end dates are required' }));
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    setSubmittingEnquiry(true);

    try {
      console.log('country---> country name in enquiry');

      const countryNameValue = localStorage.getItem('selectedCountryName') || localStorage.getItem('sessionDefaultCountryName') || cartItems[0]?.countryName || 'N/A';

      const response = await submitEnquiryMutation({
        variables: {
          name: enquiryForm.name,
          companyName: enquiryForm.company,
          email: enquiryForm.email,
          phone: enquiryForm.phone,
          address: enquiryForm.address,
          startDate: enquiryForm.campaignStartDate,
          endDate: enquiryForm.campaignEndDate,
          addedBillboardIds: formattedBillboardIds,
          companyId: companyId,
          countryId: String(cartCountryId ? cartCountryId : countryId),
          countryName: countryNameValue,
          exposure: `${import.meta.env.VITE_APP_EXPOSURE}`
        }
      });
      console.log('Enquiry response:', response);
      toast({
        title: "Success",
        description: response?.data?.submitEnquiry?.message || 'Enquiry submitted successfully!',
        variant: "default"
      });
      clearCart(); // Clear cart after successful submission
      setShowEnquiryForm(false);
      setShowSuccessModal(true);
      setSubmittingEnquiry(false);
      navigate(`${import.meta.env.VITE_APP_BASE_PATH}/explore`);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setIsSubmitting(false);
      // toast.error('An unexpected error occurred. Please try again later.');
      setSubmissionError('An unexpected error occurred. Please try again later.');
      setSubmittingEnquiry(false);

    }
  };


  // // Draft campaign submission using GraphQL
  // const saveDraftMutation = useMutation({
  //   mutationFn: async (campaignData: any) => {
  //     const token = localStorage.getItem("authToken");

  //     if (!token || !campaignId) {
  //       throw new Error("Authentication token or campaign ID not found");
  //     }

  //     console.log("Saving draft with data:", campaignData);

  //     // Prepare variables for REQUEST_FOR_APPROVAL mutation with GENERATED status (draft)
  //     const draftVariables = {
  //       campaignType: "GENERATED", // Save as draft
  //       campaignId: campaignId,
  //       input: {
  //         userId: localStorage.getItem("userId"),
  //         accessToken: token,
  //         campaignName: campaignData.name,
  //         startDate: campaignData.startDate ? format(campaignData.startDate, 'dd MMM yyyy') : null,
  //         endDate: campaignData.endDate ? format(campaignData.endDate, 'dd MMM yyyy') : null,
  //         campaignBudget: campaignData.totalBudget,
  //         campaignNote: campaignData.campaignNotes,
  //         selectedBillboards: (isLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems).map((inventory: any) => ({
  //           inventoryId: inventory.billboardId || inventory.id,
  //           inventoryName: inventory.billboardName || inventory.name,
  //           inventoryType: inventory.billboardType || inventory.type,
  //           inventoryAddress: inventory.billboardLocation || inventory.location,
  //           inventoryVenueType: inventory.billboardVenueType || "outdoor",
  //           inventoryResolution: {
  //             width: parseFloat(inventory.billboardResolutionWidth || "1920"),
  //             height: parseFloat(inventory.billboardResolutionHeight || "1080")
  //           },
  //           inventoryThumbnailUrl: inventory.billboardThumbnailUrl || inventory.image,
  //           inventoryLatitude: inventory.billboardLatitude || 0,
  //           inventoryLongitude: inventory.billboardLongitude || 0,
  //           inventoryReports: {
  //             totalPotentialViews: inventory.billboardPotentialViews || inventory.impressions || 0,
  //             averageFrequency: inventory.billboardFrequency || 1,
  //             cpm: inventory.billboardPeriodSubTotalPrice ?
  //               (inventory.billboardPeriodSubTotalPrice / ((inventory.billboardPotentialViews || 0) / 1000)) :
  //               ((inventory.price || 0) / 100),
  //             totalReach: inventory.billboardUniqueReach || inventory.impressions || 0,
  //           },
  //         }))
  //       },
  //     };

  //     const response = await client.mutate({
  //       mutation: REQUEST_FOR_APPROVAL,
  //       variables: draftVariables,
  //       fetchPolicy: "no-cache",
  //     });

  //     return response.data;
  //   },
  //   onSuccess: async (data) => {
  //     console.log("✅ saveDraftMutation.onSuccess called with data:", data);
  //     if (data?.requestForApproval?.campaign?.campaignStatus === "GENERATED") {
  //       console.log("✅ Draft status is GENERATED, proceeding with success flow");
  //       // Clean up localStorage after successful draft save
  //       localStorage.removeItem(`campaign_${campaignId}_name`);
  //       localStorage.removeItem(`campaign_${campaignId}_startDate`);
  //       localStorage.removeItem(`campaign_${campaignId}_endDate`);
  //       localStorage.removeItem('campaignName');
  //       localStorage.removeItem('startDate');
  //       localStorage.removeItem('endDate');
  //       localStorage.removeItem('selectedCampaignId');

  //       console.log("✅ localStorage cleaned, now fetching cart count...");
  //       // Fetch updated cart count from API
  //       await fetchCartCountDetails();
  //       toast({
  //         title: "Success",
  //         description: "Campaign draft saved successfully!",
  //         variant: "default"
  //       });

  //       // Don't clear cart for drafts - user can continue editing
  //       navigate("/campaigns");
  //     }
  //   },
  //   onError: (error: any) => {
  //     console.error("Draft save error:", error);
  //     toast({
  //       title: "Error",
  //       description: "Failed to save draft. Please try again.",
  //       variant: "destructive"
  //     });
  //   },
  // });

  const handleCampaignAction = async (status: string) => {
    // Prevent multiple simultaneous calls
    if (isSubmitting || loading) {
      return;
    }

    // First, require terms and conditions for REQUESTED (only if cart page is enabled)
    if (status === "REQUESTED" && isCartPageEnabled && !agreeToTerms) {
      setHighlightCheckbox(true); // Highlight checkbox
      toast({
        title: "Error",
        description: "Please agree to the terms and conditions to proceed.",
        variant: "destructive"
      });
      return;
    }
    // Then, prevent request for approval if any billboard is unavailable
    if (status === "REQUESTED") {
      const itemsToCheck = isLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems;
      const hasUnavailable = itemsToCheck.some((item: any) => !isPackageAvailable(item));

      if (hasUnavailable) {
        toast({
          title: "Error",
          description: "Please remove unavailable inventories from your cart before submitting for approval.",
          variant: "destructive"
        });
        return;
      }
    }

    const token = localStorage.getItem("authToken");
    const userNameLogin = localStorage.getItem('userNameLogin');

    // Clear highlight if terms are agreed
    setHighlightCheckbox(false);

    if (!token) {
      toast({
        title: "Error",
        description: "No authentication token found.",
        variant: "destructive"
      });
      return;
    }

    if (!campaignData) {
      toast({
        title: "Error",
        description: "No campaign data available to save.",
        variant: "destructive"
      });
      return;
    }

    if (!selectedBillboard) {
      toast({
        title: "Error",
        description: "No selected billboard found.",
        variant: "destructive"
      });
      return;
    }

    if (status === "REQUESTED") {
      setIsSubmittingForApproval(true);
    } else if (status === "Save draft") {
      setIsSavingDraft(true);
    }
    setLoading(true);

    try {
      // Remove duplicates from campaignInventories
      const uniqueCampaignInventories = campaignData.campaignInventories.filter((inventory, index, self) =>
        index === self.findIndex((inv) => inv.billboardId === inventory.billboardId)
      );

      // Determine if this is an edit campaign based on multiple indicators
      const isEditCampaign = campaignType ||
        !!campaignId;


      // When in edit mode, ensure we're using the existing campaign ID
      const campaignIdToUse = isEditCampaign ? campaignId : campaignData.id;

      if (isEditCampaign && !campaignIdToUse) {
        toast({
          title: "Error",
          description: "Campaign ID not found for edit operation",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      const approvalVariables = {
        // Use different campaign type based on whether we're editing or creating
        campaignType: isEditCampaign ? "edit-campaign" : "create-campaign",
        campaignId: campaignIdToUse,
        input: {
          accessToken: token,
          name: campaignName, // Use the current campaign name from state
          bookingSource: "ONLINE",
          startDate: campaignData.startDate,
          endDate: campaignData.endDate,
          campaignStatus: status === "Save draft" ? "GENERATED" : status,
          agency: {
            id: campaignData.agencyId,
            name: campaignData.agencyName,
          },
          company: {
            id: campaignData.companyId,
            name: campaignData.companyName,
          },
          user: {
            id: campaignData.userId,
            name: userNameLogin,
          },
          dsp: campaignData.agencyName,
          inventoriesSummary: {
            classicInventories: campaignData.inventoriesSummary.classicInventories,
            digitalInventories: campaignData.inventoriesSummary.digitalInventories,
            packages: cartItems.some(item => item.type?.toLowerCase() === 'package') ? 1 : 0,
          },
          priceSummary: {
            subTotal: campaignData.overAlltotalPrice,
            netTotal: campaignData.overAlltotalNet,
            tax: {
              name: taxLabel,
              value: campaignData.overAlltotalTax,
              percent: taxPercent,
            },
            currency: {
              country: getCurrencyCountry(),
              symbol: getCampaignCurrency(),
              code: getCampaignCurrency(),
            },
          },
          summaryReport: {
            potentialViews: campaignData.overAlltotalPotentialViews,
            uniqueReach: campaignData.overAlltotalUniqueReach,
            cpm: campaignData.overAlltotalPrice / (campaignData.overAlltotalPotentialViews / 1000),
            averageFrequency: campaignData.overAlltotalAverageFrequency,
            totalSpots: uniqueCampaignInventories.length,
          },
          reporting: {
            // Include report options in both draft and submission
            proofOfPlay: isFieldEnabled('proofOfPlay', configShowReport) ? isNegotiationCheckedProof : false,
            deliveryReports: isFieldEnabled('playLog', configShowReport) ? isNegotiationCheckedDelivery : false,
            // Commented out for now as requested
            // postCampaignReport: isFieldEnabled('postCampaignReport', configShowReport) ? isNegotiationCheckedPostCampaign : false,

          },
          campaignInventories: uniqueCampaignInventories.map((inventory) => ({
            dsp: campaignData.agencyName,
            startDate: campaignData.startDate,
            endDate: campaignData.endDate,
            companyId: campaignData.companyId,
            inventoryPrice: inventory.billboardPeriodSubTotalPrice,
            inventoryName: inventory.billboardName,
            inventoryId: inventory.billboardId,
            inventoryReferenceId: inventory.billboardReferenceId,
            inventoryType: inventory.billboardType,
            inventoryAddress: inventory.billboardAddress,
            inventoryVenueType: inventory.billboardVenueType,
            inventoryResolution: {
              width: parseFloat(inventory.billboardResolutionWidth),
              height: parseFloat(inventory.billboardResolutionHeight)
            },
            inventoryThumbnailUrl: inventory.billboardThumbnailUrl,
            inventoryLatitude: inventory.billboardLatitude,
            inventoryLongitude: inventory.billboardLongitude,
            inventoryReports: {
              totalPotentialViews: inventory.billboardPotentialViews,
              averageFrequency: inventory.billboardFrequency,
              cpm: inventory.billboardPeriodSubTotalPrice / (inventory.billboardPotentialViews / 1000),
              totalReach: inventory.billboardUniqueReach,
            },
            isSpotBased: inventory?.isSpotBased,
          }))
        },
      };

      const approvalResponse = await client.mutate({
        mutation: REQUEST_FOR_APPROVAL,
        variables: approvalVariables,
        fetchPolicy: "no-cache",
      });

      if (approvalResponse?.data?.requestForApproval?.campaign?.campaignStatus === "REQUESTED") {
        // Clean up localStorage after successful request
        localStorage.removeItem(`campaign_${campaignId}_name`);
        localStorage.removeItem(`campaign_${campaignId}_startDate`);
        localStorage.removeItem(`campaign_${campaignId}_endDate`);
        localStorage.removeItem('campaignName');
        localStorage.removeItem('startDate');
        localStorage.removeItem('endDate');
        localStorage.removeItem('selectedCampaignId');

        // ✅ CRITICAL FIX: Clear ALL country-related localStorage EXCEPT sessionDefault
        localStorage.removeItem('selectedCountryId');
        localStorage.removeItem('cartCountry');
        localStorage.removeItem('cartCountryId');
        localStorage.removeItem('navigationCountryBackup');
        localStorage.removeItem('currencyCode');
        console.log('🧹 [CART] Cleared all country data except sessionDefault after campaign request');

        // ✅ FIX: Reset serverCartCountry state to prevent false conflicts
        setServerCartCountry('empty');
        setLocalCartCountry(null);
        setHasCountryConflict(false);
        setInitialLocalCartCountry(null);
        console.log('🧹 [CART] Reset country conflict states after campaign submission');

        // ✅ FIX: Clear context cart items to prevent false conflicts
        clearCart();
        setCartList([]);
        console.log('🧹 [CART] Cleared context cartItems and cartList after campaign submission');

        console.log("Campaign requested successfully, cleared localStorage campaign data");

        // Reset dates to today for the next campaign
        resetCampaignDatesToToday();

        // Clear campaign name for the next campaign
        setCampaignName("");

        // Update cart count after successful request
        console.log("🛒 Campaign requested - updating cart count...");
        await fetchCartCountDetails();

        // Show popup instead of redirecting
        setShowAcknowledgeModal(true);
        // Don't set redirect so user stays on the current page
      }
      if (approvalResponse?.data?.requestForApproval?.campaign?.campaignStatus === "GENERATED") {
        // Clean up localStorage after successful draft save
        localStorage.removeItem(`campaign_${campaignId}_name`);
        localStorage.removeItem(`campaign_${campaignId}_startDate`);
        localStorage.removeItem(`campaign_${campaignId}_endDate`);
        localStorage.removeItem('campaignName');
        localStorage.removeItem('startDate');
        localStorage.removeItem('endDate');
        localStorage.removeItem('selectedCampaignId');

        // ✅ CRITICAL FIX: Clear ALL country-related localStorage EXCEPT sessionDefault
        localStorage.removeItem('selectedCountryId');
        localStorage.removeItem('cartCountry');
        localStorage.removeItem('cartCountryId');
        localStorage.removeItem('navigationCountryBackup');
        localStorage.removeItem('currencyCode');
        console.log('🧹 [CART] Cleared all country data except sessionDefault after draft save');

        // ✅ FIX: Reset serverCartCountry state to prevent false conflicts
        setServerCartCountry('empty');
        setLocalCartCountry(null);
        setHasCountryConflict(false);
        setInitialLocalCartCountry(null);
        console.log('🧹 [CART] Reset country conflict states after draft save');

        // ✅ FIX: Clear context cart items to prevent false conflicts
        clearCart();
        setCartList([]);
        console.log('🧹 [CART] Cleared context cartItems and cartList after draft save');

        console.log("Campaign draft saved successfully, cleared localStorage campaign data");

        // Reset dates to today for the next campaign
        resetCampaignDatesToToday();

        // Clear campaign name for the next campaign
        setCampaignName("");

        // Update cart count after successful draft save
        console.log("🛒 Draft saved - updating cart count...");
        await fetchCartCountDetails();

        // Show success toast without redirecting
        toast({
          title: "Success",
          description: "Campaign draft saved successfully!",
          variant: "default"
        });
        // window.location.href = `${import.meta.env.VITE_APP_BASE_PATH}/my-campaigns`;
        navigate(`${import.meta.env.VITE_APP_BASE_PATH}/my-campaigns`);

      }
      if (approvalResponse.errors || !approvalResponse.data.requestForApproval) {
        console.error("GraphQL Errors during approval request:", approvalResponse.errors);
        toast({
          title: "Error",
          description: "Failed to request approval: " + (approvalResponse.errors ? approvalResponse.errors.map(err => err.message).join(", ") : "Unknown error"),
          variant: "destructive"
        });
        return;
      }

      // Clean up localStorage after successful campaign save or request
      if (approvalResponse?.data?.requestForApproval?.campaign?.campaignStatus === "REQUESTED") {
        // Clear all campaign-specific localStorage data to avoid conflicts with future campaigns
        localStorage.removeItem(`campaign_${campaignId}_name`);
        localStorage.removeItem(`campaign_${campaignId}_startDate`);
        localStorage.removeItem(`campaign_${campaignId}_endDate`);
        localStorage.removeItem('campaignName');
        localStorage.removeItem('startDate');
        localStorage.removeItem('endDate');
        localStorage.removeItem('selectedCampaignId');
        console.log("Campaign saved successfully, cleared localStorage campaign data");

        // Update cart count after successful request (duplicate check)
        console.log("🛒 Campaign saved - updating cart count...");
        await fetchCartCountDetails();

        // Show success toast without redirecting
        toast({
          title: "Success",
          description: "Campaign request sent successfully!",
          variant: "default"
        });
        // window.location.href = `${import.meta.env.VITE_APP_BASE_PATH}/my-campaigns`;
        navigate(`${import.meta.env.VITE_APP_BASE_PATH}/my-campaigns`);
      }
    } catch (err) {
      console.error("Error during API calls:", err);
      toast({
        title: "Error",
        description: "An unexpected error occurred while processing your request.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setIsSubmittingForApproval(false);
      setIsSavingDraft(false);
    }
  };

  // const handleSaveDraft = () => {
  //   const draftData = {
  //     name: campaignName || "Untitled Campaign",
  //     duration: campaignDuration || "1-month", // Use calculated duration
  //     startDate: startDate || null,
  //     endDate: endDate || null,
  //     campaignNotes: campaignNotes || "",
  //     selectedBillboards: cartItems.map(item => ({
  //       id: item.id,
  //       name: item.name,
  //       price: item.price,
  //       location: item.location,
  //       impressions: item.impressions || 0,
  //       daypartSchedules: item.type === 'Digital' && item.daypartSchedules ? item.daypartSchedules : undefined
  //     })),
  //     totalBudget: Math.round(totalCost * 100), // Convert to cents
  //     status: "draft"
  //   };
  //   saveDraftMutation.mutate(draftData);
  // };

  // Empty cart view
  if (cartCount === 0) {
    // Check if we're in draft campaign mode with campaign details but no inventories
    const isDraftCampaignWithNoInventories = (
      (campaignData && campaignName) ||  // Has campaign data and name from API
      (draftCampaignId && campaignName) ||  // Has draft ID and name from URL params
      (campaignId && campaignName && edit)  // Has campaign ID, name and edit mode
    );

    if (isDraftCampaignWithNoInventories) {
      // Draft campaign with no inventories - show campaign details and "add inventories" message
      return (
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Draft Campaign Banner */}
            <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <h4 className="text-emerald-800 font-medium">Draft Campaign</h4>
                  <p className="text-emerald-700 text-sm mt-1">
                    <strong>{campaignName}</strong>
                    {startDate && endDate && (
                      <span> • {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Left Panel - No Inventories Message */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Inventories Added</h3>
                    <p className="text-gray-600 mb-6">
                      This campaign does not have inventories. To continue with this campaign, add inventories.
                    </p>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        console.log("selected country id edit---->", localStorage.getItem('selectedCountryId'))
                        const contryIdEdit = localStorage.getItem('selectedCountryId')
                        console.log("cart country id", cartCountryId);

                        if (!contryIdEdit) {
                          console.log("selected country id inside edit ---->", contryIdEdit);

                          localStorage.setItem('selectedCountryId', cartCountryId)
                        }
                        localStorage.setItem('clearStartCampaignFilters', 'true');
                        let base = '/propertylist';
                        let params = new URLSearchParams();
                        if (campaignId || draftCampaignId) {
                          params.append('draftId', campaignId || draftCampaignId || '');
                        }
                        params.append('campaignName', encodeURIComponent(campaignName || ''));
                        params.append('source', 'campaigns');
                        params.append('edit', 'true');  // Always use edit: true for draft campaigns
                        console.log("Draft campaign params-->", params.toString());
                        navigate(`${base}?${params.toString()}`);
                      }}
                    >
                      Start Campaign
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Panel - Campaign Details */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 sticky top-8">
                  <h3 className="text-lg font-semibold mb-4">Campaign Details</h3>

                  {/* Campaign Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                    <input
                      type="text"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  {/* Campaign Dates */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <div className="text-sm text-gray-600">
                        {startDate ? format(startDate, 'MMM dd, yyyy') : 'Not set'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <div className="text-sm text-gray-600">
                        {endDate ? format(endDate, 'MMM dd, yyyy') : 'Not set'}
                      </div>
                    </div>
                  </div>

                  {/* Campaign Summary */}
                  <div className="border-t pt-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Inventories:</span>
                        <span className="font-medium">0</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Cost:</span>
                        <span className="font-medium">{currencyCode} 0.00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Regular empty cart - show generic empty cart UI
      // Check login config from Landing Page
      const landingPage = config?.find((page: any) => page.name === "Landing Page");
      const landingConfigs = landingPage && Array.isArray(landingPage.configurations) ? landingPage.configurations : [];
      // If config is not loaded yet, don't render login button (prevents flicker)
      const configLoaded = landingPage && landingConfigs.length > 0;
      const loginConfig = landingConfigs.find((cfg: any) => cfg.key === "login");
      const isLoginEnabled = loginConfig ? loginConfig.default !== false : true;

      return (
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 className="h-12 w-12 text-gray-400" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h1>
              <p className="text-gray-600 mb-8">
                Add some billboards to your cart to get started with your campaign.
              </p>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => {
                  localStorage.removeItem('selectedCountryId');
                  localStorage.removeItem('cartCountry');
                  localStorage.removeItem('cartCountryId');
                  localStorage.removeItem('navigationCountryBackup');
                  localStorage.removeItem('currencyCode');
                  console.log('🧹 [CART] Cleared all country data except sessionDefault on button click');
                  // ✅ FIX: Sync country filter before navigation
                  const countryForFilter = cartCountryId || localStorage.getItem('selectedCountryId') || localStorage.getItem('sessionDefaultCountryId');
                  if (countryForFilter) {
                    localStorage.setItem('selectedCountryId', countryForFilter);
                    localStorage.setItem('sessionDefaultCountryId', countryForFilter);
                  }

                  if (isLoggedIn) {
                    localStorage.setItem('clearStartCampaignFilters', 'true');
                    let base = '/propertylist';
                    let params = new URLSearchParams();
                    console.log("test Navigating with campaignId-->", campaignId);
                    console.log("test Navigating with edit-->", edit);


                    if (edit && campaignId) {
                      console.log("Navigating with campaignId-->", campaignId);
                      params.append('draftId', campaignId);
                    }
                    params.append('campaignName', encodeURIComponent(campaignName || ''));
                    params.append('source', 'campaigns');
                    params.append('edit', edit ? 'true' : 'false');
                    console.log("params-->", params.toString());
                    console.log("combination url=-->", `${base}?${params.toString()}`);
                    navigate(`${base}?${params.toString()}`);
                  } else {
                    // Only navigate to explore if it's enabled, otherwise redirect to home
                    if (isExploreEnabled) {
                      navigate(`${import.meta.env.VITE_APP_BASE_PATH}/explore`);
                    } else {
                      navigate(`${import.meta.env.VITE_APP_BASE_PATH}/`);
                    }
                  }
                }}
              >
                {isLoggedIn ? "Start Campaign" : (isExploreEnabled ? "Browse Billboards" : "Go to Home")}
              </Button>
              {/* Only show login button if config is loaded and login is enabled */}
              {configLoaded && !isLoggedIn && isLoginEnabled && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate("/login")}
                    className="border-2 border-primary-600 text-primary-600 hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-500 hover:text-white hover:border-transparent transition-all duration-300"
                  >
                    Login
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  // Success page view
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {isLoggedIn ? "Campaign Submitted Successfully!" : "Enquiry Submitted Successfully!"}
            </h1>
            <p className="text-gray-600 mb-8">
              {isLoggedIn
                ? "Your campaign has been submitted for approval. You'll receive an email confirmation shortly."
                : "Thank you for your interest! We'll review your enquiry and get back to you with a detailed proposal within 24 hours."
              }
            </p>

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => {
                setShowSuccess(false);
                if (isLoggedIn) {
                  navigate("/campaigns");
                } else {
                  // Only navigate to explore if it's enabled, otherwise redirect to home
                  navigate(isExploreEnabled ? "/explore" : "/");
                }
              }}>
                {isLoggedIn ? "View My Campaigns" : (isExploreEnabled ? "Browse More Billboards" : "Go to Home")}
              </Button>
              <Link to="/">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // At the top of the return statement, before the main content
  if (isSyncing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  // Helper function to calculate maximum lead time from all digital/classic billboards
  const getMaxLeadTime = () => {
    const itemsToCheck = effectiveIsLoggedIn && cartList?.length > 0 ? cartList : cartItems;
    let maxLeadTime = 0;
    
    itemsToCheck.forEach((item: any) => {
      const isDigital = (item.type === 'Digital' || item.type === 'digital') ||
                        (item.billboardType === 'Digital' || item.billboardType === 'digital');
      const isClassic = (item.type === 'Classic' || item.type === 'classic') ||
                       (item.billboardType === 'Classic' || item.billboardType === 'classic');
      
      if ((isDigital || isClassic) && !item.isDeal) {
        const leadTime = item.leadTime || 0;
        if (leadTime > maxLeadTime) {
          maxLeadTime = leadTime;
        }
      }
    });
    
    return maxLeadTime;
  };


  // Country conflict modal handlers
  const handleClearCartAndContinue = async () => {
    console.log('[COUNTRY CONFLICT] User chose to clear cart and continue with server cart');

    // Clear local cart data
    localStorage.removeItem('billboardCart');
    localStorage.removeItem('preLoginCartData');
    localStorage.removeItem('preLoginExploreFlowData');
    localStorage.removeItem('exploreBillboardArray');
    localStorage.removeItem('packageCart');
    localStorage.removeItem('preLoginPackageData');
    localStorage.removeItem('explorePackageArray');
    localStorage.removeItem('packageBillboardCart');
    localStorage.removeItem('dealCart');

    // Clear cart context
    clearCart();

    // Reset conflict state
    setHasCountryConflict(false);
    setShowCountryConflictModal(false);
    setLocalCartCountry(null);

    // Refresh cart data to show server cart
    await fetchCartList();
    await fetchCartCountDetails();

    toast({
      title: "Cart Cleared",
      description: "Your local cart has been cleared. You can now continue with your existing campaign.",
    });
  };

  const handleKeepCart = async () => {
    console.log('[COUNTRY CONFLICT] User chose to keep their cart (Overwrite Server)');
    const token = localStorage.getItem("authToken");

    if (!token) {
      console.error("No auth token found for keep cart operation");
      return;
    }

    // Set syncing true to show loader/block interactions
    setIsSyncing(true);

    try {
      // 1. Delete ALL items currently on the server (The conflicting items)
      if (cartList.length > 0) {
        console.log("[COUNTRY CONFLICT] Preparing to delete server items:", cartList.length);

        // Construct delete items payload directly from cartList
        const itemsToDelete = cartList.map((item: any) => ({
          id: item.cartItemId,
          itemType: item.isDraft ? "draft-data" : "cart-data"
        }));

        // Call mutation directly
        const deleteResponse = await client.mutate({
          mutation: DELETE_CART_PAGE,
          variables: {
            accessToken: token,
            deleteItems: itemsToDelete
          },
          fetchPolicy: "no-cache",
        });

        const success = deleteResponse?.data?.deleteCartItem?.success;
        if (!success) {
          throw new Error(deleteResponse?.data?.deleteCartItem?.message || "Failed to delete server items");
        }

        console.log("[COUNTRY CONFLICT] Server items deleted successfully.");

        // Clear local state referencing the deleted server items
        setCartList([]);
        setCartItemCount(0);
      }

      // 2. Sync the local items to the now-empty server cart
      // We pass 'true' for ignoreConflictState to bypass the check we just resolved
      console.log('[COUNTRY CONFLICT] Triggering sync to upload local items:', localCartCountry);

      await syncLocalStorageCartToAPI(true, localCartCountry);

      // 3. Reset Conflict UI & Update Context
      setHasCountryConflict(false);
      setShowCountryConflictModal(false);
      setServerCartCountry(null);

      // Update country context to match the new items
      if (localCartCountry) {
        localStorage.setItem('selectedCountryId', localCartCountry);
        setCartCountry(localCartCountry);
        localStorage.setItem('sessionDefaultCountryId', localCartCountry);
      }

      toast({
        title: "Cart Updated",
        description: "Your previous cart has been replaced with your current selection.",
        variant: "default"
      });

    } catch (error) {
      console.error('[COUNTRY CONFLICT] Error resolving conflict:', error);
      toast({
        title: "Error",
        description: "Failed to update cart. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Main cart view
  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 py-4 sm:py-8 relative">
        {/* Loader Overlay - covers header and entire cart section */}
        {(isSubmittingForApproval || isSavingDraft || submittingEnquiry) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 rounded-lg">
            <div className="text-center">
              <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600 font-medium">
                {isSubmittingForApproval ? 'Submitting for approval...' : isSavingDraft ? 'Saving draft...' : 'Processing...'}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-emerald-600">Shopping Cart</h1>
            <p className="text-gray-600 mt-2">
              {cartCount} {cartCount === 1 ? 'Inventory' : 'Inventories'} selected
            </p>
          </div>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => {
              localStorage.setItem('clearStartCampaignFilters', 'true');

              // ✅ FIX: Sync country filter to selectedCountryId before navigation
              if (cartCountryId) {
                localStorage.setItem('selectedCountryId', cartCountryId);
                localStorage.setItem('sessionDefaultCountryId', cartCountryId);
              }

              if (effectiveIsLoggedIn) {
                let base = '/propertylist';
                let params = new URLSearchParams();
                if (edit && campaignId) {
                  params.append('draftId', campaignId);
                  params.append('campaignName', encodeURIComponent(campaignName || ''));
                  params.append('edit', 'true');
                  params.append('source', source || 'cart');
                }
                const url = params.toString() ? `${base}?${params.toString()}` : base;
                window.location.href = `${window.location.origin}${url}`;
              } else {
                // Only navigate to explore if it's enabled, otherwise redirect to home
                if (isExploreEnabled) {
                  navigate(`${base_path}/explore`);
                } else {
                  navigate(`${base_path}/`);
                }
              }
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Continue Shopping
          </Button>
        </div>

        {/* Global Error Message for Spot Updates */}
        {spotsUpdateError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <Info className="h-4 w-4 text-red-600 mr-2" />
              <p className="text-red-700 text-sm font-medium">{spotsUpdateError}</p>
            </div>
          </div>
        )}

        {/* Draft Campaign Notice */}
        {draftCampaignId && effectiveIsLoggedIn && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
              <div className="flex-1">
                <h4 className="text-emerald-800 font-medium">Continuing Draft Campaign</h4>
                <p className="text-emerald-700 text-sm mt-1">
                  <strong>{campaignName}</strong>
                  {startDate && endDate && (
                    <span> • {formatDate(startDate).replace('Sept', 'Sep')} - {formatDate(endDate).replace('Sept', 'Sep')}</span>
                  )}
                </p>
                <p className="text-emerald-600 text-xs mt-1">
                  You can add more inventories to this campaign or modify the details below.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left 2/3: Metrics + Selected Inventories stacked with no extra margin */}
          <div className="lg:col-span-2 flex flex-col gap-0">
            {/* Metrics */}
            {(isLoggedIn || import.meta.env.VITE_APP_MOCK_DATA === 'true') && (
              <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6 flex gap-x-8">
                {isFieldEnabled("impressions", configQuick) && (
                  <div className="flex flex-col items-center flex-1">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-gray-500 text-sm">Impressions</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                            <div className="space-y-1">
                              <p className="font-medium text-white">
                                Total number of audiences who may have the opportunity to see the ad during the ad play which includes repeat view.
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                      {campaignData?.overAlltotalPotentialViews?.toLocaleString() || totalImpressions?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                )}
                {isFieldEnabled("reach", configQuick) && (
                  <div className="flex flex-col items-center flex-1">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-gray-500 text-sm">Unique Reach</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                            <div className="space-y-1">
                              <p className="font-medium text-white">
                                Unique number of audiences who passed by the billboard.
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                      {campaignData?.overAlltotalUniqueReach?.toLocaleString() || totalUniqueReach?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                )}
                {isFieldEnabled("average frequency", configQuick) && (
                  <div className="flex flex-col items-center flex-1">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-gray-500 text-sm">Average Frequency</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                            <div className="space-y-1">
                              <p className="font-medium text-white">
                                An average number of times a person has the opportunity to see the site during the campaign duration.
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                      {campaignData?.overAlltotalAverageFrequency?.toFixed(2) || totalAverageFrequency?.toFixed(2) || 'N/A'}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Selected Inventories */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <h2 className="text-lg sm:text-xl font-semibold">Selected Inventories</h2>
                <Button variant="ghost" size="sm" onClick={handleClearAllClick} className="text-red-600 w-full sm:w-auto">
                  Clear All
                </Button>
              </div>

              <div className="space-y-6">
                {/* Group billboards by type like original design */}
                {(() => {
                  // For logged-in users, use API data (cartList), for non-logged-in users use local cart (cartItems)
                  const itemsToDisplay = effectiveIsLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems;

                  if (!itemsToDisplay || itemsToDisplay.length === 0) {
                    return (
                      <div className="text-center text-gray-600 py-8">
                        No billboards in your cart.
                      </div>
                    );
                  }

                  const groupedBillboards = itemsToDisplay.reduce((acc: any, billboard: any) => {
                    // Handle both API data and local cart data
                    const type = billboard.billboardType || billboard.type || 'Other';
                    const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
                    if (!acc[capitalizedType]) acc[capitalizedType] = [];
                    acc[capitalizedType].push(billboard);
                    return acc;
                  }, {});

                  const typeOrder = ['Deal', 'Digital', 'Classic', 'Package', 'Transit', 'Other'];
                  const orderedTypes = typeOrder.filter(type => groupedBillboards[type]);

                  return orderedTypes.map((type) => (
                    <div key={type} className="space-y-3">
                      {/* Type Header */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                          <h3 className={`text-base sm:text-lg font-semibold ${type === 'Digital' ? 'text-blue-600' :
                            type === 'Classic' ? 'text-green-600' :
                              type === 'Package' ? 'text-purple-600' :
                                type === 'Transit' ? 'text-orange-600' :
                                  type === 'Deal' ? 'text-emerald-600' :
                                    'text-gray-600'
                            }`}>
                            {type === 'Deal' ? 'Special Deals' : `${type} Inventories`}
                          </h3>
                          <Badge variant="secondary" className={
                            type === 'Digital' ? 'bg-blue-100 text-blue-700' :
                              type === 'Classic' ? 'bg-green-100 text-green-700' :
                                type === 'Package' ? 'bg-purple-100 text-purple-700' :
                                  type === 'Transit' ? 'bg-orange-100 text-orange-700' :
                                    type === 'Deal' ? 'bg-emerald-100 text-emerald-700' :
                                      'bg-gray-100 text-gray-700'
                          }>
                            {groupedBillboards[type].length} {groupedBillboards[type].length === 1 ? 'item' : 'items'}
                          </Badge>
                        </div>
                        <div className={`text-sm font-semibold ${type === 'Digital' ? 'text-blue-600' :
                          type === 'Classic' ? 'text-green-600' :
                            type === 'Package' ? 'text-purple-600' :
                              type === 'Transit' ? 'text-orange-600' :
                                type === 'Deal' ? 'text-emerald-600' :
                                  'text-gray-600'
                          }`}>
                          Total: {calculateSectionTotal(groupedBillboards, type)}
                        </div>
                      </div>

                      {/* Items in this type */}
                      <div className={`space-y-3 pl-4 border-l-2 ${type === 'Digital' ? 'border-blue-200' :
                        type === 'Classic' ? 'border-green-200' :
                          type === 'Package' ? 'border-purple-200' :
                            type === 'Transit' ? 'border-orange-200' :
                              type === 'Deal' ? 'border-emerald-200' :
                                'border-gray-200'
                        }`} style={{ maxHeight: 580, overflowY: 'auto' }}>
                        {groupedBillboards[type].map((billboard: any, index: number) => (
                          <div key={billboard.billboardId || billboard.id || index} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg bg-gray-50 hover:bg-white transition-colors">
                            <img
                              src={
                                billboard.billboardThumbnailUrl ||
                                billboard.image ||
                                "data:image/svg+xml,%3Csvg width='150' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='150' height='100' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af'%3EBillboard%3C/text%3E%3C/svg%3E"
                              }
                              alt={billboard.billboardName || billboard.name || "Billboard"}
                              className="w-full sm:w-20 h-32 sm:h-16 object-cover rounded"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "data:image/svg+xml,%3Csvg width='150' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='150' height='100' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af'%3EBillboard%3C/text%3E%3C/svg%3E";
                              }}
                            />
                            <div className="flex-1 w-full">
                              <h4 className="font-medium text-sm sm:text-base mb-2">
                                {billboard.billboardName || billboard.name}
                              </h4>
                              <div className="flex items-center justify-between gap-4 mb-1">
                                <div className="flex flex-col gap-1">
                                  <p className="text-sm text-gray-600">
                                    {billboard.billboardLocation || billboard.location || billboard?.state || billboard?.billboardCountry || "Location not specified"}
                                  </p>
                                  <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                                    By {((billboard.billboardType?.toLowerCase() === 'package' || billboard.type?.toLowerCase() === 'package') || billboard.isDeal)
                                      ? 'Multiple Media Owners'
                                      : (getMediaOwner(billboard.mediaOwnerName)?.name || billboard.mediaOwnerName)}
                                  </p>
                                </div>

                                {/* Digital billboard spot controls and calculation in same row */}
                                {(() => {
                                  const isDigital = (billboard?.type?.toLowerCase() === 'digital') ||
                                    (billboard?.billboardType?.toLowerCase() === 'digital');

                                  const isSpotBased = billboard?.isSpotBased || false;
                                  if (!isDigital || !isSpotBased || billboard.isDeal) return null;

                                  // Check if this billboard supports spot selection
                                  const supportsSpots = billboard.minimumSpotsPerDay !== null && billboard.minimumSpotsPerDay !== undefined;

                                  // Only show controls if billboard supports spots
                                  if (!supportsSpots && effectiveIsLoggedIn) return null;
                                  if (!effectiveIsLoggedIn && !supportsSpots) return null; // Local cart always shows if digital

                                  const minSpotsPerDay = billboard.minimumSpotsPerDay || 1;
                                  const maxSpotsPerDay = billboard.maximumSpotDurationPerDay || 1000;

                                  // Use state for API items, prioritize userSelectedSpots from API, fallback to minimum spots for new items
                                  let currentSpots;
                                  if (isLoggedIn && billboard.cartItemId) {
                                    // If apiCartSpots has a value, use it (user has made changes)
                                    if (apiCartSpots[billboard.cartItemId] !== undefined) {
                                      currentSpots = apiCartSpots[billboard.cartItemId];
                                    }
                                    // If no local state but API has userSelectedSpots, use it
                                    else if (billboard.userSelectedSpots !== undefined && billboard.userSelectedSpots !== null) {
                                      currentSpots = billboard.userSelectedSpots;
                                    }
                                    // Fallback to minimum
                                    else {
                                      currentSpots = minSpotsPerDay;
                                    }
                                  } else {
                                    currentSpots = billboard.spots || minSpotsPerDay;
                                  }
                                  return (
                                    <div className="flex items-center gap-4">
                                      {/* Spot controls */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-400 font-medium text-left">Spots/day</span>
                                        <div className="flex items-center gap-2">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const newValue = Math.max(1, currentSpots - 1);
                                              if (effectiveIsLoggedIn && billboard.cartItemId) {
                                                setApiCartSpots(prev => ({ ...prev, [billboard.cartItemId]: newValue }));
                                                debouncedUpdateSpots(billboard.billboardId, newValue, billboard.billboardType);
                                              } else if (typeof billboard.id === 'number') {
                                                updateSpots(billboard.id, newValue);
                                              }
                                            }}
                                            disabled={currentSpots <= 1}
                                            className="h-8 w-8 p-0"
                                          >
                                            <Minus className="h-3 w-3" />
                                          </Button>
                                          <input
                                            type="number"
                                            min="1"
                                            max={maxSpotsPerDay}
                                            value={currentSpots}
                                            onChange={(e) => {
                                              const newValue = parseInt(e.target.value) || 1;
                                              if (newValue >= 1) {
                                                if (isLoggedIn && billboard.cartItemId) {
                                                  setApiCartSpots(prev => ({ ...prev, [billboard.cartItemId]: newValue }));
                                                  debouncedUpdateSpots(billboard.billboardId, newValue, billboard.billboardType);
                                                } else if (typeof billboard.id === 'number') {
                                                  updateSpots(billboard.id, newValue);
                                                }
                                              }
                                            }}
                                            className="w-16 text-center text-sm font-medium border rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          />
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const newValue = currentSpots + 1;
                                              if (isLoggedIn && billboard.cartItemId) {
                                                setApiCartSpots(prev => ({ ...prev, [billboard.cartItemId]: newValue }));
                                                debouncedUpdateSpots(billboard.billboardId, newValue, billboard.billboardType);
                                              } else if (typeof billboard.id === 'number') {
                                                updateSpots(billboard.id, newValue);
                                              }
                                            }}
                                            className="h-8 w-8 p-0"
                                          >
                                            <Plus className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>

                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-gray-500 mt-1">
                                {isFieldEnabled('impressions', configQuick) && (
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 font-medium">Impressions</span>
                                    <span className="text-gray-600">
                                      {(isLoggedIn || import.meta.env.VITE_APP_MOCK_DATA === 'true')
                                        ? (billboard.billboardPotentialViews || billboard.impressions || 0)?.toLocaleString()
                                        : 'Login to view Impressions'
                                      }
                                    </span>
                                  </div>
                                )}
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-400 font-medium">
                                    {(billboard.billboardType?.toLowerCase() === 'classic' || billboard.type?.toLowerCase() === 'classic')
                                      ? 'Size' : 'Resolution'
                                    }
                                  </span>
                                  <span className="text-gray-600">
                                    {(billboard.billboardResolutionWidth && billboard.billboardResolutionHeight)
                                      ? `${billboard.billboardResolutionWidth} x ${billboard.billboardResolutionHeight}`
                                      : (
                                        billboard.specifications?.resolution && billboard.specifications.resolution !== 'N/A'
                                          ? billboard.specifications.resolution
                                          : (billboard.size || 'N/A')
                                      )
                                    }
                                  </span>
                                </div>
                                {isLoggedIn && (billboard.validityEndDate || billboard.packageDetails?.validityEndDate) && (
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 font-medium">Package Duration</span>
                                    <span className="text-gray-600">
                                      {(() => {
                                        console.log("billboard package validity----------------------->", billboard);

                                        // Prioritize validity dates from packageDetails (original data) over API response
                                        // Use the dates from the API response (cartList item)
                                        const validityStart = billboard.validityStartDate;
                                        const validityEnd = billboard.validityEndDate;

                                        // Check if dates are valid before formatting
                                        if (!validityStart || !validityEnd) {
                                          return 'N/A';
                                        }

                                        return `${new Date(validityStart)
                                          .toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                          .replace('Sept', 'Sep')} - ${new Date(validityEnd)
                                            .toLocaleDateString('en-GB', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric',
                                            })
                                            .replace('Sept', 'Sep')}`;
                                      })()}
                                    </span>
                                  </div>
                                )}



                                {billboard.spotDuration && (
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 font-medium">Spot Duration</span>
                                    <span className="text-gray-600">{billboard.spotDuration}s</span>
                                  </div>
                                )}
                                {/* {billboard.billboardReferenceId && (
                                  <div className="flex flex-col">
                                    <span className="text-xs text-gray-400 font-medium">ID</span>
                                    <span className="text-gray-600">{billboard.billboardReferenceId}</span>
                                  </div>
                                )} */}
                              </div>
                              <div className="mt-2">
                                <Badge
                                  variant="secondary"
                                  className={`${(billboard.billboardType || billboard.type) === 'digital' || (billboard.billboardType || billboard.type) === 'Digital' ? 'bg-blue-100 text-blue-700' :
                                    (billboard.billboardType || billboard.type) === 'classic' || (billboard.billboardType || billboard.type) === 'Classic' ? 'bg-green-100 text-green-700' :
                                      (billboard.billboardType || billboard.type) === 'Package' ? 'bg-purple-100 text-purple-700' :
                                        (billboard.billboardType || billboard.type) === 'Transit' ? 'bg-orange-100 text-orange-700' :
                                          (billboard.isDeal || (billboard.billboardType || billboard.type) === 'Deal') ? 'bg-emerald-100 text-emerald-700' :
                                            'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                  {
                                    (() => {
                                      const type = billboard.billboardType || billboard.type || (billboard.isDeal ? 'Deal' : 'Other');
                                      return type.charAt(0).toUpperCase() + type.slice(1);
                                    })()
                                  }
                                </Badge>
                                {(() => {
                                  // For packages, always show availability based on validity dates
                                  // For digital/classic, only show if availableBooking is defined
                                  const isPackage = (billboard.billboardType?.toLowerCase() === "package" || billboard.type?.toLowerCase() === "package") || billboard.isDeal;
                                  const shouldShowBadge = isPackage || billboard.availableBooking !== undefined;

                                  if (shouldShowBadge) {
                                    const available = isPackageAvailable(billboard);
                                    return (
                                      <>
                                        <Badge
                                          variant="secondary"
                                          className={`ml-2 ${available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                                        >
                                          {available ? 'Available' : 'Unavailable'}
                                        </Badge>
                                        {!available && (
                                          <div className="text-xs bg-red-100 text-red-700 rounded px-2 py-1 mt-1 inline-block">⚠️ The selected start and end dates exceed the allowed booking period. Please select dates within the allowed range</div>
                                        )}
                                      </>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>

                              {/* Digital billboard validation messages */}
                              {(() => {
                                const isDigital = (billboard.type === 'Digital' || billboard.type === 'digital') ||
                                  (billboard.billboardType === 'Digital' || billboard.billboardType === 'digital');
                                const isClassic = (billboard.type === 'Classic' || billboard.type === 'classic') ||
                                  (billboard.billboardType === 'Classic' || billboard.billboardType === 'classic');

                                if ((!isDigital && !isClassic) || billboard.isDeal) return null;

                                // Check if this billboard supports spot selection
                                const supportsSpots = billboard.minimumSpotsPerDay !== null && billboard.minimumSpotsPerDay !== undefined;
                                const supportsSellingTerms = billboard.customizeSellingTerms === true;

                                // Only show validation messages if billboard supports spots OR selling terms OR has lead time
                                if (!supportsSpots && !supportsSellingTerms && !billboard.leadTime && isLoggedIn) return null;
                                if (!isLoggedIn && !supportsSpots) return null;

                                // Spot validation logic (only validation messages, controls are moved up next to name)
                                let spotValidationSection = null;
                                if (supportsSpots || !isLoggedIn) {
                                  const minSpotsPerDay = billboard.minimumSpotsPerDay || 1;
                                  const maxSpotsPerDay = billboard.maximumSpotDurationPerDay || 1000;

                                  // Use state for API items, prioritize userSelectedSpots from API, fallback to minimum spots for new items
                                  let currentSpots;
                                  if (isLoggedIn && billboard.cartItemId) {
                                    // If apiCartSpots has a value, use it (user has made changes)
                                    if (apiCartSpots[billboard.cartItemId] !== undefined) {
                                      currentSpots = apiCartSpots[billboard.cartItemId];
                                    }
                                    // If no local state but API has userSelectedSpots, use it
                                    else if (billboard.userSelectedSpots !== undefined && billboard.userSelectedSpots !== null) {
                                      currentSpots = billboard.userSelectedSpots;
                                    }
                                    // Fallback to minimum
                                    else {
                                      currentSpots = minSpotsPerDay;
                                    }
                                  } else {
                                    currentSpots = billboard.spots || minSpotsPerDay;
                                  }

                                  // Spot validation errors
                                  const spotBelowMin = supportsSpots && currentSpots < minSpotsPerDay;
                                  const spotAboveMax = currentSpots > maxSpotsPerDay;

                                  spotValidationSection = (
                                    <>
                                      {/* Spot validation errors */}
                                      {spotBelowMin && (
                                        <div className="text-xs bg-yellow-100 text-yellow-800 rounded px-2 py-1 inline-block border border-yellow-300">
                                          ⚠️ Minimum booking spot is {minSpotsPerDay}. Please increase the number of spot to proceed.
                                        </div>
                                      )}
                                      {spotAboveMax && (
                                        <div className="text-xs bg-yellow-100 text-yellow-800 rounded px-2 py-1 inline-block border border-yellow-300">
                                          ⚠️ Maximum booking spot is {maxSpotsPerDay}. Please decrease the number of spot to proceed.
                                        </div>
                                      )}
                                    </>
                                  );
                                }

                                // Campaign duration validation (only if selling terms are customizable)
                                let durationSection = null;
                                if (supportsSellingTerms) {
                                  const minSellingDays = billboard?.minimumSellingDays || 1;
                                  const currentCampaignDays = campaignDuration || 1;
                                  const durationBelowMin = currentCampaignDays < minSellingDays;

                                  if (durationBelowMin) {
                                    durationSection = (
                                      <div className="text-xs bg-yellow-100 text-yellow-800 rounded px-2 py-1 inline-block border border-yellow-300">
                                        ⚠️ Minimum booking period is {minSellingDays} days. Please increase the number of days to proceed.
                                      </div>
                                    );
                                  }
                                }

                                // Lead time validation for digital/classic billboards
                                let leadTimeSection = null;
                                if ((isDigital || isClassic) && billboard.leadTime && startDate) {
                                  const leadTimeDays = billboard.leadTime;
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  
                                  const minStartDate = new Date(today);
                                  minStartDate.setDate(minStartDate.getDate() + leadTimeDays);
                                  
                                  const selectedStartDate = new Date(startDate);
                                  selectedStartDate.setHours(0, 0, 0, 0);
                                  
                                  if (selectedStartDate < minStartDate) {
                                    leadTimeSection = (
                                      <div className="text-xs bg-yellow-100 text-yellow-800 rounded px-2 py-1 inline-block border border-yellow-300">
                                        ⚠️ Selected start date does not meet the required lead time. Start date must be on or after {format(minStartDate, 'dd MMM yyyy')} (Lead time: {leadTimeDays} days).
                                      </div>
                                    );
                                  }
                                }

                                // Only show if we have either spot validation, duration, or lead time content
                                if (!spotValidationSection && !durationSection && !leadTimeSection) return null;

                                return (
                                  <div className="mt-3 space-y-2">
                                    {spotValidationSection}
                                    {durationSection}
                                    {leadTimeSection}
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                              <div className="text-right">
                                {/* Spot calculation formula - above price */}
                                {(() => {
                                  const isDigital = (billboard?.type?.toLowerCase() === 'digital') ||
                                    (billboard.billboardType?.toLowerCase() === 'digital');

                                  if (!isDigital || billboard.isDeal) return null;

                                  // Check if this billboard supports spot selection
                                  const supportsSpots = billboard.minimumSpotsPerDay !== null && billboard.minimumSpotsPerDay !== undefined;

                                  // Only show calculation if billboard supports spots
                                  if (!supportsSpots && isLoggedIn) return null;
                                  if (!isLoggedIn && !supportsSpots) return null;

                                  // Use state for API items, prioritize userSelectedSpots from API, fallback to minimum spots for new items
                                  let currentSpots;
                                  if (isLoggedIn && billboard.cartItemId) {
                                    // If apiCartSpots has a value, use it (user has made changes)
                                    if (apiCartSpots[billboard.cartItemId] !== undefined) {
                                      currentSpots = apiCartSpots[billboard.cartItemId];
                                    }
                                    // If no local state but API has userSelectedSpots, use it
                                    else if (billboard.userSelectedSpots !== undefined && billboard.userSelectedSpots !== null) {
                                      currentSpots = billboard.userSelectedSpots;
                                    }
                                    // Fallback to minimum
                                    else {
                                      currentSpots = billboard.minimumSpotsPerDay || 1;
                                    }
                                  } else {
                                    currentSpots = billboard.spots || billboard.minimumSpotsPerDay || 1;
                                  }

                                  return billboard.singleSpotCost ? (
                                    <div className="text-xs text-gray-500 mb-2 text-right">
                                      {billboard.currencyCode} {billboard.singleSpotCost.toFixed(2)} / spot × {currentSpots} spots × {campaignDuration || 1} days
                                    </div>
                                  ) : null;
                                })()}
                                <p className="font-semibold">
                                  {calculateBillboardPrice(billboard)}
                                </p>
                                {/* Only show "campaign total" for Digital and Classic billboards */}
                                {/* {(() => {
                                  const billboardType = billboard.billboardType || billboard.type;
                                  const isDigital = billboardType === 'Digital' || billboardType === 'digital';
                                  const isClassic = billboardType === 'Classic' || billboardType === 'classic';

                                  return (isDigital || isClassic) && !billboard.isDeal ? (
                                    <p className="text-sm text-gray-500">
                                      campaign total
                                    </p>
                                  ) : null;
                                })()} */}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (isLoggedIn) {
                                    // Handle API item removal using GraphQL delete
                                    handleDeleteItem(billboard.cartItemId || billboard.billboardId, billboard.isDraft || false);
                                  } else {
                                    // Handle local cart item removal
                                    removeFromCart(billboard.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Right Panel - Campaign Form for Authenticated Users or Lead Form for Non-Authenticated */}
          <div className="space-y-6">
            {effectiveIsLoggedIn ? (
              <>
                {/* Campaign Details Form */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4">Campaign Details</h3>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="campaignName">Campaign Name</Label>
                      <Input
                        id="campaignName"
                        value={campaignName}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow up to 25 characters
                          if (value.length <= 26) {
                            setCampaignName(value);
                          }
                        }}
                        placeholder="Enter campaign name"
                        maxLength={26}
                      />
                      {/* Validation message for empty campaign name */}
                      {!campaignName.trim() && (
                        <div className="text-sm text-red-600 mt-1">
                          Campaign Name is required.
                        </div>
                      )}
                      {/* Validation message for character limit */}
                      {campaignName.length === 26 && (
                        <div className="text-sm text-red-600 mt-1">
                          Maximum 25 characters reached.
                        </div>
                      )}
                    </div>

                    {/* Dynamic Campaign Duration Section */}
                    {(() => {
                      const needsManualDates = requiresCampaignDates();
                      const automaticDates = getLocalAutomaticCampaignDates();
                      const breakdown = getInventoryTypeBreakdown();
                      const hasMixedTypes = hasInventoryTypeMix();

                      if (!needsManualDates && automaticDates) {
                        // Only packages/deals - show automatic dates
                        return (
                          <div>
                            <Label>Campaign Duration</Label>
                            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                              <div className="flex items-center gap-2 text-emerald-700">
                                <Info className="h-4 w-4" />
                                <span className="text-sm font-medium">Automatically set based on package/deal content</span>
                              </div>
                              <div className="mt-2 text-sm text-emerald-600">
                                <strong>Start:</strong> {formatDate(automaticDates.startDate)} → <strong>End:</strong> {formatDate(automaticDates.endDate)}
                              </div>
                              <div className="mt-1 text-xs text-emerald-600">
                                Duration: {startDate && endDate ? calculateDuration(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')) : calculateDuration(automaticDates.startDate, automaticDates.endDate)}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Digital/Classic require manual dates
                      return (
                        <div>
                          <Label>Campaign Duration</Label>
                          {/* {hasMixedTypes && (
                            <div className="mt-1 mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                              <Info className="h-3 w-3 inline mr-1" />
                              Mixed inventory detected. Set dates for Digital/Classic items.
                              Package/deal dates are auto-managed.
                            </div>
                          )} */}
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <input
                              type="date"
                              value={startDate ? format(startDate, 'yyyy-MM-dd') : defaultExploreStartDate}
                              min={format(new Date(), 'yyyy-MM-dd')}
                              max={endDate ? format(endDate, 'yyyy-MM-dd') : undefined}
                              onChange={e => {
                                const val = e.target.value;
                                if (!val) {
                                  handleStartDateChange(null);
                                } else {
                                  handleStartDateChange(new Date(val));
                                }
                              }}
                              onMouseDown={e => {
                                e.preventDefault();
                                if (e.currentTarget.showPicker) e.currentTarget.showPicker();
                              }}
                              placeholder="Start Date"
                              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <input
                              type="date"
                              value={endDate ? format(endDate, 'yyyy-MM-dd') : defaultExploreEndDate}
                              min={startDate ? format(startDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')}
                              onChange={e => {
                                const val = e.target.value;
                                if (!val) {
                                  handleEndDateChange(null);
                                } else {
                                  handleEndDateChange(new Date(val));
                                }
                              }}
                              onMouseDown={e => {
                                e.preventDefault();
                                if (e.currentTarget.showPicker) e.currentTarget.showPicker();
                              }}
                              placeholder="End Date"
                              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          {campaignDuration && (
                            <div className="mt-2 text-sm text-emerald-600 font-medium">
                              Duration: {campaignDuration}
                            </div>
                          )}
                          {durationError && (
                            <div className="mt-2 text-sm text-red-600 font-medium">
                              {durationError}
                            </div>
                          )}

                        </div>
                      );
                    })()}

                    {(isFieldEnabled('proofOfPlay', configShowReport) || isFieldEnabled('playLog', configShowReport) || isFieldEnabled('postCampaignReport', configShowReport)) && (
                      (isFieldEnabled('show_report_in_cart_page', configShowReport) &&
                        (isFieldEnabled('proofOfPlay', configShowReport) || isFieldEnabled('playLog', configShowReport) || isFieldEnabled('postCampaignReport', configShowReport))
                      ) && (
                        <div>
                          <Label>Delivery Report Options</Label>
                          <div className="space-y-2 mt-1">
                            {isFieldEnabled('proofOfPlay', configShowReport) && (
                              <label className="flex items-center space-x-2">
                                <Checkbox
                                  checked={isNegotiationCheckedProof}
                                  onCheckedChange={(checked: boolean) => handleProof({ target: { checked } })}
                                />
                                <span className="text-sm">Proof of Play</span>
                              </label>
                            )}
                            {isFieldEnabled('playLog', configShowReport) && (
                              <label className="flex items-center space-x-2">
                                <Checkbox
                                  checked={isNegotiationCheckedDelivery}
                                  onCheckedChange={(checked: boolean) => handleDelivery({ target: { checked } })}
                                />
                                <span className="text-sm">Delivery Report</span>
                              </label>
                            )}
                            {/* Commented out as requested
                            {isFieldEnabled('postCampaignReport', configShowReport) && (
                              <label className="flex items-center space-x-2">
                                <Checkbox 
                                  checked={isNegotiationCheckedPostCampaign}
                                  onCheckedChange={(checked: boolean) => handlePostCampaign({ target: { checked }})}
                                />
                                <span className="text-sm">CI Report</span>
                              </label>
                            )}
                            */}
                          </div>
                        </div>
                      )
                    )}

                    {/* <div>
                      <Label htmlFor="campaignNotes">Campaign Notes (Optional)</Label>
                      <Textarea
                        id="campaignNotes"
                        value={campaignNotes}
                        onChange={(e) => setCampaignNotes(e.target.value)}
                        placeholder="Add any special requirements or notes..."
                        rows={3}
                      />
                    </div> */}
                  </div>
                </div>

                {/* Campaign Summary */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4">Campaign Summary</h3>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Inventories:</span>
                      <span className="font-medium">
                        {isLoggedIn && cartList?.length ? cartList.length : cartCount}
                      </span>
                    </div>

                    {/* Breakdown by inventory type */}
                    {/* {(() => {
                      const groupedBillboards = cartItems.reduce((acc, billboard) => {
                        const type = typeof billboard?.type === 'string' ? billboard.type.toLowerCase() : 'Other';
                        if (!acc[type]) acc[type] = { count: 0, cost: 0, impressions: 0 };
                        acc[type].count += 1;
                        // Calculate cost based on billboard type
                        if (billboard.isDeal) {
                          // Deals have fixed pricing
                          acc[type].cost += (billboard.price / 100);
                        } else if (billboard.type === "Digital" && billboard.spots && billboard.campaignDuration) {
                          // Digital: spots per day × campaign duration
                          acc[type].cost += ((billboard.price / 100) * billboard.spots * billboard.campaignDuration);
                        } else if (billboard.type === "Classic" && billboard.campaignDuration) {
                          // Classic: monthly price ÷ 30 days × campaign duration
                          const monthlyRate = billboard.price / 100;
                          const dailyRate = monthlyRate / 30;
                          acc[type].cost += (dailyRate * billboard.campaignDuration);
                        } else {
                          // Package and Transit use monthly pricing
                          acc[type].cost += (billboard.price / 100);
                        }
                        acc[type].impressions += billboard.impressions || 0;
                        return acc;
                      }, {} as Record<string, { count: number; cost: number; impressions: number }>);

                      return Object.keys(groupedBillboards).length > 1 ? (
                        <div className="space-y-2 py-2 border rounded-lg bg-gray-50 px-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">Breakdown by Type:</div>
                          {Object.entries(groupedBillboards).map(([type, data]) => (
                            <div key={type} className="flex justify-between text-sm">
                              <span className="text-gray-600">{type}: {data.count} billboard{data.count !== 1 ? 's' : ''}</span>
                              <span className="font-medium">{currencyCode} {data.cost.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()} */}

                    {/* Dynamic Campaign Duration Display */}
                    {(() => {
                      const needsManualDates = requiresCampaignDates();
                      const automaticDates = getLocalAutomaticCampaignDates();

                      if (!needsManualDates && automaticDates) {
                        // Show auto-determined dates for packages/deals
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Campaign Duration:</span>
                              <span className="font-medium">
                                {isLoggedIn && campaignData?.totalDuration
                                  ? `${campaignData.totalDuration} days`
                                  : calculateDuration(automaticDates.startDate, automaticDates.endDate)
                                }
                              </span>
                            </div>
                            <div className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                              Automatically set: {formatDate(automaticDates.startDate)} to {formatDate(automaticDates.endDate)}
                            </div>
                          </div>
                        );
                      } else if (campaignDuration && startDate && endDate) {
                        // Show manually set dates for digital/classic
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Campaign Duration:</span>
                              <span className="font-medium">
                                {isLoggedIn && campaignData?.totalDuration
                                  ? `${campaignData.totalDuration} days`
                                  : `${campaignDuration} days`
                                }
                              </span>
                            </div>
                          </div>
                        );
                      } else if (needsManualDates) {
                        // Show placeholder when dates not set for digital/classic
                        return (
                          <div className="flex justify-between text-gray-500">
                            <span>Campaign Duration:</span>
                            <span className="italic">Set campaign dates above</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {(isLoggedIn || import.meta.env.VITE_APP_MOCK_DATA === 'true') ? (
                      <div className="flex justify-between">
                        {isFieldEnabled('impressions', configQuick) && (
                          <span>Total Impressions:</span>
                        )}
                        {isFieldEnabled('impressions', configQuick) && (
                          <span className="font-medium">
                            {campaignData?.overAlltotalPotentialViews?.toLocaleString() || totalImpressions?.toLocaleString()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-center text-xs text-gray-600">
                          <Lock className="mr-2 h-3 w-3" />
                          <span>Login to view full audience and performance data</span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-medium">
                        {isLoggedIn && campaignData?.overAlltotalPrice
                          ? formatPrice(campaignData.overAlltotalPrice, getCampaignCurrency())
                          : formatPrice(currentTotalCost || totalCost, currencyCode)
                        }
                      </span>
                    </div>
                    {isLoggedIn && campaignData?.overAlltotalTax ? (
                      <div className="flex justify-between">
                        <span>{gstValue} ({percentage}%)</span>
                        <span className="font-medium">
                          {formatPrice(campaignData.overAlltotalTax, getCampaignCurrency())}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span>{gstValue} ({percentage}%)</span>
                        <span className="font-medium">
                          {formatPrice((currentTotalCost || totalCost || 0) * (taxPercent || 0) / 100, getCampaignCurrency())}
                        </span>
                      </div>
                    )}

                    <div className="border-t pt-3">
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Net Total:</span>
                        <span className="text-emerald-600">
                          {isLoggedIn && campaignData?.overAlltotalNet
                            ? formatPrice(campaignData.overAlltotalNet, getCampaignCurrency())
                            : formatPrice((currentTotalCost || totalCost) + ((currentTotalCost || totalCost) * (taxPercent ? taxPercent : (parseFloat(percentage) || 0)) / 100), currencyCode)
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    {isCartPageEnabled && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="terms"
                          checked={agreeToTerms}
                          onCheckedChange={() => {
                            if (agreeToTerms) {
                              setAgreeToTerms(false);
                              setHighlightCheckbox(false);
                            } else {
                              setShowTerms(true);
                            }
                          }}
                        />
                        <label htmlFor="terms" className="text-sm">
                          I agree to the{" "}
                          <button
                            type="button"
                            onClick={() => setShowTerms(true)}
                            className="text-emerald-600 hover:underline"
                          >
                            Terms & Conditions
                          </button>
                        </label>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={(() => {
                          const needsManualDates = requiresCampaignDates();
                          const hasValidDates = needsManualDates ? (startDate && endDate) : true;
                          const itemsToCheck = isLoggedIn && cartList && cartList.length > 0 ? cartList : cartItems;
                          const hasUnavailable = itemsToCheck.some((item: any) => !isPackageAvailable(item));

                          // Check for spot validation errors in digital billboards
                          const hasSpotErrors = itemsToCheck.some((item: any) => {
                            const isDigital = (item.type === 'Digital' || item.type === 'digital') ||
                              (item.billboardType === 'Digital' || item.billboardType === 'digital');

                            if (!isDigital || item.isDeal) return false;

                            const supportsSpots = item.minimumSpotsPerDay !== null && item.minimumSpotsPerDay !== undefined;
                            if (!supportsSpots && isLoggedIn) return false;
                            if (!isLoggedIn && !supportsSpots) return false;

                            const minSpotsPerDay = item.minimumSpotsPerDay || 1;
                            const maxSpotsPerDay = item.maximumSpotDurationPerDay || 1000;

                            // Get current spots for this item
                            let currentSpots;
                            if (isLoggedIn && item.cartItemId) {
                              if (apiCartSpots[item.cartItemId] !== undefined) {
                                currentSpots = apiCartSpots[item.cartItemId];
                              } else if (item.userSelectedSpots !== undefined && item.userSelectedSpots !== null) {
                                currentSpots = item.userSelectedSpots;
                              } else {
                                currentSpots = minSpotsPerDay;
                              }
                            } else {
                              currentSpots = item.spots || minSpotsPerDay;
                            }

                            // Check for spot validation errors
                            const spotBelowMin = supportsSpots && currentSpots < minSpotsPerDay;
                            const spotAboveMax = currentSpots > maxSpotsPerDay;

                            return spotBelowMin || spotAboveMax;
                          });

                          // Check for duration validation errors in digital/classic billboards
                          const hasDurationErrors = itemsToCheck.some((item: any) => {
                            const isDigital = (item.type === 'Digital' || item.type === 'digital') ||
                              (item.billboardType === 'Digital' || item.billboardType === 'digital');

                            if (!isDigital || item.isDeal) return false;

                            const supportsSellingTerms = item.customizeSellingTerms === true;
                            if (!supportsSellingTerms) return false;

                            const minSellingDays = item.minimumSellingDays || 1;
                            const currentCampaignDays = campaignDuration || 1;

                            return currentCampaignDays < minSellingDays;
                          });

                          // Check for lead time validation errors
                          const hasLeadTimeErrors = (() => {
                            if (!startDate) return false;
                            
                            const maxLeadTime = getMaxLeadTime();
                            if (maxLeadTime === 0) return false;
                            
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const selectedStartDate = new Date(startDate);
                            selectedStartDate.setHours(0, 0, 0, 0);
                            
                            const minimumStartDate = new Date(today);
                            minimumStartDate.setDate(today.getDate() + maxLeadTime);
                            
                            return selectedStartDate < minimumStartDate;
                          })();

                          return (isCartPageEnabled && !agreeToTerms) || cartCount === 0 || !hasValidDates || !campaignName.trim() || campaignName.length === 25 || !!durationError || hasUnavailable || hasSpotErrors || hasDurationErrors || hasLeadTimeErrors;
                        })()}
                        onClick={() => { handleCampaignAction("REQUESTED") }}
                      >
                        Submit for Approval
                      </Button>

                      {!edit && (
                        <Button
                          variant="outline"
                          className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          disabled={cartCount === 0 || !campaignName.trim()}
                          onClick={() => { handleCampaignAction("Save draft") }}
                        >
                          Save as Draft
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Lead Form for Non-Authenticated Users */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4">Continue Your Campaign</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {isLoginEnabled
                      ? "Login to create and manage your Inventory campaign directly, or request a custom proposal."
                      : "Request a custom proposal."
                    }
                  </p>

                  <div className="space-y-3">
                    {isLoginEnabled && (
                      <Button
                        className="text-white w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          localStorage.setItem('redirectAfterLogin', '/mycart');
                          navigate('/login');
                        }}
                      >
                        Login to Continue Campaign
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      onClick={() => {
                        // const { start, end } = getCartDateRange(cartList);
                        const start = startDate ? format(startDate, 'yyyy-MM-dd') : moment().format("YYYY-MM-DD");
                        const end = endDate ? format(endDate, 'yyyy-MM-dd') : moment().endOf('month').format("YYYY-MM-DD");
                        setEnquiryForm(form => ({
                          ...form,
                          campaignStartDate: start,
                          campaignEndDate: end,
                        }));
                        setShowEnquiryForm(true);
                      }}
                    >
                      Request Custom Proposal
                    </Button>
                  </div>
                </div>

                {/* Cart Summary */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4">Selection Summary</h3>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Inventories:</span>
                      <span className="font-medium">{cartCount}</span>
                    </div>

                    {/* Breakdown by inventory type */}
                    {/* {(() => {
                      console.log("cartItems --->", cartItems);

                      const groupedBillboards = cartItems.reduce((acc, billboard) => {
                        const type = billboard.type || 'Other';
                        if (!acc[type]) acc[type] = { count: 0, cost: 0, impressions: 0 };
                        acc[type].count += 1;
                        // Calculate cost based on billboard type
                        if (billboard.isDeal) {
                          // Deals have fixed pricing
                          acc[type].cost += (billboard.price / 100);
                        } else if (billboard.type === "Digital" && billboard.spots && billboard.campaignDuration) {
                          // Digital: spots per day × campaign duration  
                          acc[type].cost += ((billboard.price / 100) * billboard.spots * billboard.campaignDuration);
                        } else if (billboard.type === "Classic" && billboard.campaignDuration) {
                          // Classic: monthly price ÷ 30 days × campaign duration
                          const monthlyRate = billboard.price / 100;
                          const dailyRate = monthlyRate / 30;
                          acc[type].cost += (dailyRate * billboard.campaignDuration);
                        } else {
                          // Package and Transit use monthly pricing
                          acc[type].cost += (billboard.price / 100);
                        }
                        acc[type].impressions += billboard.impressions || 0;
                        return acc;
                      }, {} as Record<string, { count: number; cost: number; impressions: number }>);

                      return Object.keys(groupedBillboards).length > 1 ? (
                        <div className="space-y-2 py-2 border rounded-lg bg-gray-50 px-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">Breakdown by Type:</div>
                          {Object.entries(groupedBillboards).map(([type, data]) => (
                            <div key={type} className="flex justify-between text-sm">
                              <span className="text-gray-600">{type}: {data.count} billboard{data.count !== 1 ? 's' : ''}</span>
                              <span className="font-medium">{currencyCode} {data.cost.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()} */}
                    <div>
                      <Label>Campaign Duration</Label>

                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <input
                          type="date"
                          value={startDate ? format(startDate, 'yyyy-MM-dd') : defaultExploreStartDate}
                          min={format(new Date(), 'yyyy-MM-dd')}
                          max={endDate ? format(endDate, 'yyyy-MM-dd') : undefined}
                          onChange={e => {
                            const val = e.target.value;
                            if (!val) {
                              handleStartDateChange(null);
                            } else {
                              handleStartDateChange(new Date(val));
                            }
                          }}
                          onMouseDown={e => {
                            e.preventDefault();
                            if (e.currentTarget.showPicker) e.currentTarget.showPicker();
                          }}
                          placeholder="Start Date"
                          className="w-full border rounded px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <input
                          type="date"
                          value={endDate ? format(endDate, 'yyyy-MM-dd') : defaultExploreEndDate}
                          min={startDate ? format(startDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')}
                          onChange={e => {
                            const val = e.target.value;
                            if (!val) {
                              handleEndDateChange(null);
                            } else {
                              handleEndDateChange(new Date(val));
                            }
                          }}
                          onMouseDown={e => {
                            e.preventDefault();
                            if (e.currentTarget.showPicker) e.currentTarget.showPicker();
                          }}
                          placeholder="End Date"
                          className="w-full border rounded px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-500" // <-- add pr-8
                        />
                      </div>
                    </div>
                    {/* Dynamic Campaign Duration Display */}
                    {(() => {
                      const needsManualDates = requiresCampaignDates();
                      const automaticDates = getLocalAutomaticCampaignDates();

                      if (!needsManualDates && automaticDates) {
                        // Show auto-determined dates for packages/deals
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Campaign Duration:</span>
                              <span className="font-medium">
                                {isLoggedIn && campaignData?.totalDuration
                                  ? `${campaignData.totalDuration} days`
                                  : startDate && endDate ? calculateDuration(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')) : calculateDuration(automaticDates.startDate, automaticDates.endDate)
                                }
                              </span>
                            </div>
                            {/* <div className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                              Automatically set: {automaticDates.startDate} to {automaticDates.endDate}
                            </div> */}
                          </div>
                        );
                      } else if (campaignDuration && startDate && endDate) {
                        // Show manually set dates for digital/classic
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Campaign Duration:</span>
                              <span className="font-medium">
                                {isLoggedIn && campaignData?.totalDuration
                                  ? `${campaignData.totalDuration} days`
                                  : `${campaignDuration} days`
                                }
                              </span>
                            </div>
                            {isLoggedIn && (
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                Selected dates: {startDate ? format(startDate, 'MMM dd, yyyy') : 'Not set'} to {endDate ? format(endDate, 'MMM dd, yyyy') : 'Not set'}
                              </div>
                            )}
                          </div>
                        );
                      } else if (!isLoggedIn && cartItems.length > 0) {
                        // For non-authenticated users, always show today to month-end duration
                        return (
                          <div className="flex justify-between">
                            <span>Campaign Duration:</span>
                            <span className="font-medium">{campaignDuration} days</span>
                          </div>
                        );
                      } else if (needsManualDates) {
                        // Show placeholder when dates not set for digital/classic
                        return (
                          <div className="flex justify-between text-gray-500">
                            <span>Campaign Duration:</span>
                            <span className="italic">Set campaign dates above</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-medium">
                        {isLoggedIn && campaignData?.overAlltotalPrice
                          ? formatPrice(campaignData.overAlltotalPrice, getCampaignCurrency())
                          : formatPrice(totalCost, currencyCode)
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{gstValue} ({percentage}%)</span>
                      <span className="font-medium">
                        {formatPrice(
                          (isLoggedIn && campaignData?.overAlltotalPrice ? campaignData.overAlltotalPrice : currentTotalCost) * (parseFloat(percentage) || 0) / 100,
                          getCampaignCurrency()
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Net Total:</span>
                      <span className="font-medium text-emerald-600">{formatPrice(totalCost + (totalCost || 0) * (taxPercent ? taxPercent : (parseFloat(percentage) || 0)) / 100, getCampaignCurrency())}</span>
                    </div>
                    {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-center text-xs text-gray-600">
                        <Lock className="mr-2 h-3 w-3" />
                        <span>Login to view full audience and performance data</span>
                      </div>
                    </div> */}
                  </div>

                  <div className="mt-6">
                    {isLoginEnabled && (
                      <>
                        <Link to="/login">
                          <Button variant="outline" className="w-full mb-2 text-emerald-600 hover:bg-emerald-50">
                            Login to Create Campaign
                          </Button>
                        </Link>
                        <p className="text-xs text-gray-500 text-center">
                          Already have an account? Login to start your campaign immediately.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Country Conflict Modal */}
      <Dialog open={showCountryConflictModal} onOpenChange={setShowCountryConflictModal}>
        <DialogContent
          className="sm:max-w-[480px] p-0 gap-0 overflow-hidden border-0 shadow-xl [&>button]:hidden"
          onPointerDownOutside={(e) => {
            e.preventDefault();
            toast({
              title: "Action Required",
              description: "Please select an option to proceed.",
              variant: "destructive"
            });
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            toast({
              title: "Action Required",
              description: "Please select an option to proceed.",
              variant: "destructive"
            });
          }}
        >
          {/* Header Section with Icon */}
          <div className="p-6 pb-2 flex flex-col items-center text-center">
            <div className="h-14 w-14 bg-amber-100 rounded-full flex items-center justify-center mb-5 ring-8 ring-amber-50 transition-all">
              <AlertTriangle className="h-7 w-7 text-amber-600" strokeWidth={2} />
            </div>

            <DialogHeader className="space-y-2">
              <DialogTitle className="text-xl font-bold text-gray-900 text-center">
                Country Conflict Detected
              </DialogTitle>
            </DialogHeader>

            <div className="mt-4 text-sm text-gray-600 leading-relaxed">
              <p>
                Your cart contains items from <span className="font-bold text-gray-900 px-1 bg-gray-100 rounded">{localCartCountry}</span>,
                but your account has an existing campaign in <span className="font-bold text-gray-900 px-1 bg-gray-100 rounded">{serverCartCountry}</span>.
              </p>
            </div>
          </div>

          {/* Action Section */}
          <div className="p-6 pt-2 space-y-6">
            <div className="text-center text-sm font-medium text-gray-500">
              Please choose which items you want to keep:
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleKeepCart}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 text-base shadow-sm ring-offset-2 focus:ring-2 focus:ring-emerald-500"
              >
                Keep {localCartCountry} Items
                <span className="ml-2 opacity-80 text-xs font-normal">(Overwrite Server)</span>
              </Button>

              <Button
                onClick={handleClearCartAndContinue}
                variant="outline"
                className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 h-11 text-base"
              >
                Keep {serverCartCountry} Items
                <span className="ml-2 text-gray-400 text-xs font-normal">(Clear Local Cart)</span>
              </Button>
            </div>
          </div>

          {/* Footer Note */}
          <div className="bg-amber-50 p-4 border-t border-amber-100 flex gap-3 items-start">
            <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-5">
              <strong>Note:</strong> Choosing to keep your local cart ({localCartCountry}) will prevent syncing with your account until the conflict is resolved.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Form Modal */}
      <Dialog open={showEnquiryForm} onOpenChange={setShowEnquiryForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Custom Proposal</DialogTitle>
          </DialogHeader>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmitEnquiry(e); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={enquiryForm.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEnquiryForm({ ...enquiryForm, name: value });
                    // Real-time validation
                    const error = validateField('name', value);
                    setValidationErrors(prev => 
                      error ? { ...prev, name: error } : { ...prev, name: '' }
                    );
                  }}
                  placeholder="Enter your full name"
                  className={validationErrors.name ? 'border-red-500' : ''}
                  required
                />
                {/* Validation message */}
                {validationErrors.name && (
                  <div className="text-sm text-red-600 mt-1">
                    {validationErrors.name}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={enquiryForm.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEnquiryForm({ ...enquiryForm, email: value });
                    // Real-time validation
                    const error = validateField('email', value);
                    setValidationErrors(prev => 
                      error ? { ...prev, email: error } : { ...prev, email: '' }
                    );
                  }}
                  placeholder="Enter your email"
                  className={validationErrors.email ? 'border-red-500' : ''}
                  required
                />
                {/* Validation message */}
                {validationErrors.email && (
                  <div className="text-sm text-red-600 mt-1">
                    {validationErrors.email}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="space-y-2">
                  <div className="flex w-full gap-2">
                    {/* Country Code Dropdown - Searchable */}
                    <div className="relative" ref={countryDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        disabled={countryCodeLoading}
                        className="w-32 h-10 px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm disabled:opacity-50 flex items-center justify-between"
                      >
                        <span className="truncate">{enquiryCountryCode}</span>
                        <span className="ml-1 flex-shrink-0">▼</span>
                      </button>

                      {/* Dropdown Menu */}
                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 min-w-80">
                          {/* Search Input */}
                          <input
                            type="text"
                            placeholder="Search by code or country..."
                            value={enquiryCountrySearchQuery}
                            onChange={(e) => setEnquiryCountrySearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setShowCountryDropdown(false);
                              }
                            }}
                          />

                          {/* Options List */}
                          <div className="max-h-64 overflow-y-auto">
                            {countryCodeOptions.length > 0 ? (
                              countryCodeOptions
                                .filter((item: any) =>
                                  item.code.toLowerCase().includes(enquiryCountrySearchQuery.toLowerCase()) ||
                                  item.country.toLowerCase().includes(enquiryCountrySearchQuery.toLowerCase())
                                )
                                .map((item: any) => (
                                  <button
                                    key={item.code + item.country}
                                    type="button"
                                    onClick={() => {
                                      setEnquiryCountryCode(item.code);
                                      setEnquiryCountryName(item.country);
                                      setShowCountryDropdown(false);
                                      setEnquiryCountrySearchQuery("");
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm flex justify-between items-center gap-3"
                                  >
                                    <span className="font-medium min-w-12">{item.code}</span>
                                    <span className="text-gray-600 flex-1">{item.country}</span>
                                  </button>
                                ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Phone Number Input */}
                    <div className="flex-1">
                      <Input
                        type="text"
                        placeholder="Phone Number"
                        value={enquiryForm.phone}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEnquiryForm({ ...enquiryForm, phone: value });
                          // Real-time validation
                          const error = validateField('phone', value);
                          setValidationErrors(prev => 
                            error ? { ...prev, phone: error } : { ...prev, phone: '' }
                          );
                        }}
                        required
                        className={`w-full h-10 px-4 bg-white border rounded-md text-gray-900 placeholder-gray-500 placeholder:font-light focus:border-emerald-500 focus:ring-emerald-500 focus:ring-0 focus:outline-none ${
                          validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </div>
                  </div>
                  {/* Phone validation message */}
                  {validationErrors.phone && (
                    <div className="text-sm text-red-600 mt-1">
                      {validationErrors.phone}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={enquiryForm.company}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEnquiryForm({ ...enquiryForm, company: value });
                    // Real-time validation
                    const error = validateField('company', value);
                    setValidationErrors(prev => 
                      error ? { ...prev, company: error } : { ...prev, company: '' }
                    );
                  }}
                  placeholder="Enter your company name"
                  className={validationErrors.company ? 'border-red-500' : ''}
                />
                {/* Company validation message */}
                {validationErrors.company && (
                  <div className="text-sm text-red-600 mt-1">
                    {validationErrors.company}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={enquiryForm.address}
                onChange={(e) => {
                  const value = e.target.value;
                  setEnquiryForm({ ...enquiryForm, address: value });
                  // Real-time validation
                  const error = validateField('address', value);
                  setValidationErrors(prev => 
                    error ? { ...prev, address: error } : { ...prev, address: '' }
                  );
                }}
                placeholder="Enter your address"
                className={validationErrors.address ? 'border-red-500' : ''}
              />
              {/* Address validation message */}
              {validationErrors.address && (
                <div className="text-sm text-red-600 mt-1">
                  {validationErrors.address}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Preferred Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={enquiryForm.campaignStartDate}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, campaignStartDate: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="endDate">Preferred End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={enquiryForm.campaignEndDate}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, campaignEndDate: e.target.value })}
                />
              </div>
            </div>

            {/* <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={enquiryForm.notes}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, notes: e.target.value })}
                placeholder="Tell us about your campaign goals, target audience, or any specific requirements..."
                rows={3}
              />
            </div> */}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEnquiryForm(false)} disabled={submittingEnquiry}>
                Cancel
              </Button>
              <Button type="submit" className="text-white bg-emerald-600 hover:bg-emerald-700" disabled={submittingEnquiry}>
                {submittingEnquiry ? "Submitting..." : "Submit Enquiry"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions Modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col p-0 gap-0 rounded-lg overflow-hidden">
          {/* Sticky Green Label Only */}
          <div className="sticky top-0 z-10 bg-white px-6 pt-6 pb-4 border-b rounded-t-lg">
            <DialogTitle className="text-emerald-600 text-center">Terms & Conditions</DialogTitle>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
            <h3 className="font-semibold mb-2">Terms and Conditions</h3>
            {termss ? (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: termss }}
              />
            ) : (
              // Fallback static content if termss is not available
              <>
                <div>
                  <h4 className="font-medium mb-2">Introduction</h4>
                  <p>These Terms and Conditions govern your use of our billboard advertising platform and services.</p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Campaign Terms</h4>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All campaigns are subject to approval by our content review team</li>
                    <li>Payment is required before campaign activation</li>
                    <li>Campaign modifications may incur additional fees</li>
                    <li>Cancellations must be made 48 hours before campaign start date</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Content Guidelines</h4>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Content must comply with local advertising standards</li>
                    <li>No offensive, discriminatory, or illegal content</li>
                    <li>Content must be provided in required formats and specifications</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* Fixed Footer at Bottom */}
          <div className="bg-white px-6 py-4 border-t flex justify-end shrink-0 rounded-b-lg">
            <Button
              onClick={() => {
                setAgreeToTerms(true);
                setShowTerms(false);
                setHighlightCheckbox(false);
              }}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
            >
              Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 text-center">
              Are you sure you want to remove this inventory from your cart?
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button className="border bg-red-600 hover:bg-red-700 text-white"
              variant="destructive"
              onClick={confirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete All Confirmation Modal */}
      <Dialog open={showDeleteAllModal} onOpenChange={setShowDeleteAllModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Clear All</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <b>all</b> items from your cart? This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteAllModal(false)}
            >
              Cancel
            </Button>
            <Button className="border bg-red-600 hover:bg-red-700 text-white"
              variant="destructive"
              onClick={confirmDeleteAll}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Deleting..." : "Delete All"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}